// PDF report generation for the Project Wizard.
// Uses html2canvas + jsPDF to capture an off-screen DOM rendering and produce
// a multi-page A4 PDF with ZAMBA branding.

import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import React from "react";
import { createRoot } from "react-dom/client";
import { withLightTheme } from "./pdfTheme";
import { buildUnitSchedule, isPrimaryAir } from "./unitSchedule";
import { configurationScheduleHTML, variantSummaryHTML } from "./configurationSchedule";
// PDF-2 Rule 5: drawings live ONLY in the Submittal Package (assembled by
// SubmittalPackageTab.jsx). They are no longer appended to the Selection
// Report by this module, so the imports are gone.
// import { drawingsForRooms, fetchDrawingBytes } from "./submittalDrawings";
import { withVariantTags } from "./variantTags";
import { paSensibleCapacity, paLatentCapacity, dewpointF } from "./psychro";
import { fmtKind as fmtKindUnit, unitLabel as unitLabelUnit, toSI } from "./units";
import { designConditionsHTML, drawDesignConditionsToPdf } from "./pdfDesignConditions";
import { formatSizingFootnote, isFACB, isACB, isEWS, getRoomStaticPressureInwg, MODEL_D_CAPACITY_DERATE } from "./wizardCalc";

// Module-level isSI flag — set by the entry-point exports
// (generateProjectSummaryPDF / generateSubmittalPDF) so all HTML/text builders
// can produce numbers in the right unit system without threading the flag
// through every helper. SAFE because each PDF generation is awaited
// sequentially within a single page lifecycle.
let _isSI = false;

// Format a number in IP (default) or SI when the kind is known.
//   • fmtCap(v) → capacity (BTU/h or W)
//   • fmtFlow(v) → CFM or L/s
//   • fmtTemp(v) → °F or °C
//   • fmtWaterFlow(v) → GPM or L/s
//   • fmtPressure(v) → ftH₂O or kPa
//   • fmtStatic(v) → in. wg or Pa
//   • fmtLen(v)  → ft or m
// All inputs are IP-stored values; the helpers convert at the boundary.
const fmtCap = (v, d) => fmtKindUnit(v, "capacity", _isSI, d);
const fmtFlow = (v, d) => fmtKindUnit(v, "flow", _isSI, d);
const fmtTemp = (v, d) => fmtKindUnit(v, "temp", _isSI, d);
const fmtWaterFlow = (v, d) => fmtKindUnit(v, "waterFlow", _isSI, d);
const fmtPressure = (v, d) => fmtKindUnit(v, "pressure", _isSI, d);
const fmtStatic = (v, d) => fmtKindUnit(v, "staticPressure", _isSI, d);
const fmtLen = (v, d) => fmtKindUnit(v, "length", _isSI, d);
const uL = (kind) => unitLabelUnit(kind, _isSI);

// Locale-aware date formatter:
//   IP → "May 15, 2026" (en-US long)
//   SI → "15 May 2026"  (en-GB long)
function fmtDate(d = new Date()) {
  return d.toLocaleDateString(_isSI ? "en-GB" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const LOGO_URL = window.location.origin + "/zamba-logo.png";

async function preloadLogo() {
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
      img.src = LOGO_URL;
    });
    const c = document.createElement("canvas");
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    c.getContext("2d").drawImage(img, 0, 0);
    return c.toDataURL("image/png");
  } catch (e) {
    return null;
  }
}

function fmtNum(v, digits = 0) {
  if (v === null || v === undefined || v === "" || isNaN(parseFloat(v))) return "—";
  return parseFloat(v).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function modelLabel(room, raw) {
  if (!room?.unit_model) return "—";
  const isN = ["Model N", "Model N-f"].includes(room.unit_model);
  if (isN) return room.unit_model;
  const len =
    raw?.system_parameters?.length_ft ||
    raw?.results?.[0]?.length_ft ||
    null;
  if (!len) return room.unit_model;
  // Nominal SI length: 1 ft = 300 mm (catalogue convention).
  const lenStr = _isSI ? `${Math.round(parseFloat(len) * 300)}` : `${len}`;
  return `${room.unit_model}-${lenStr}`;
}

// --- helper: render an off-screen HTML container and append PDF pages from it ---
// opts:
//   appendToCurrentPage  → first slice draws on the CURRENT page (don't addPage first)
//   antiOrphanSelector   → CSS selector for "subtitle" elements that must NOT
//                          orphan at the bottom of a page. If a slice would end
//                          within N pixels after such an element, the slice is
//                          shortened to push the subtitle onto the next page so
//                          it stays in sequence with at least ~5 lines of body.
async function renderHTMLToPdf(pdf, html, opts = {}) {
  const container = document.createElement("div");
  container.style.cssText =
    "position:absolute;left:-9999px;top:0;width:794px;background:#ffffff;color-scheme:light;font-family:system-ui,sans-serif;color:#0f172a;padding:24px;";
  container.innerHTML = html;
  // Force light mode before appending so dark: variants never fire on the
  // off-screen container. withLightTheme restores the original theme on exit.
  const canvas = await withLightTheme(async () => {
    document.body.appendChild(container);
    // Two frames so the browser recalculates styles in light mode before capture.
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
    const scale = 1.5;
    return html2canvas(container, {
      scale,
      useCORS: true,
      backgroundColor: "#ffffff",
      onclone: (doc) => {
        doc.documentElement.classList.remove("dark");
        doc.body.classList.remove("dark");
      },
    });
  });
  // Move the finally-cleanup outside the light-theme wrapper — body removal
  // doesn't need the theme forced.
  try {
    const scale = 1.5;
    const pageWmm = pdf.internal.pageSize.getWidth();
    const pageHmm = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const targetW = pageWmm - margin * 2;
    const ratio = targetW / canvas.width;
    const fullHmm = canvas.height * ratio;

    // Collect anti-orphan anchor positions in CANVAS coordinates.
    // Reserve at least 5 lines (~14px each at 11pt) below each anchor; if the
    // page would cut within that reserve, push the anchor to the next page.
    const lineHeightPx = 14 * scale;
    const minRunPx = 5 * lineHeightPx; // ~5 lines below subtitle on same page
    const anchors = [];
    if (opts.antiOrphanSelector) {
      container.querySelectorAll(opts.antiOrphanSelector).forEach((el) => {
        const rect = el.getBoundingClientRect();
        const cRect = container.getBoundingClientRect();
        anchors.push((rect.top - cRect.top) * scale);
      });
    }

    // Slice the tall canvas into page-sized chunks (JPEG-compressed)
    let offsetY = 0;
    let isFirstSlice = true;
    while (offsetY < canvas.height) {
      const pageContentHpx = (pageHmm - margin * 2) / ratio;
      let sliceHpx = Math.min(pageContentHpx, canvas.height - offsetY);

      // Anti-orphan adjustment: if a subtitle anchor falls within the last
      // `minRunPx` of this slice, cut the slice just BEFORE the subtitle so
      // the subtitle and at least 5 lines of body land on the next page.
      const naturalEnd = offsetY + sliceHpx;
      for (const a of anchors) {
        if (a > offsetY + 4 && a < naturalEnd && naturalEnd - a < minRunPx) {
          sliceHpx = Math.max(40, a - offsetY - 4); // leave 4px guard
          break;
        }
      }

      const slice = document.createElement("canvas");
      slice.width = canvas.width;
      slice.height = sliceHpx;
      slice.getContext("2d").drawImage(
        canvas, 0, offsetY, canvas.width, sliceHpx,
        0, 0, canvas.width, sliceHpx
      );
      const sliceImg = slice.toDataURL("image/jpeg", 0.85);

      // Page management: only add a NEW page if this is NOT the first slice
      // OR the caller explicitly asked us to start a fresh page.
      if (!isFirstSlice || !opts.appendToCurrentPage) {
        pdf.addPage();
      }
      isFirstSlice = false;

      pdf.addImage(sliceImg, "JPEG", margin, margin, targetW, sliceHpx * ratio, undefined, "FAST");

      // Footer
      const footer = `Generated by ZAMBA Product Selector — ${fmtDate()} — www.zambahvac.com`;
      pdf.setFontSize(7);
      pdf.setTextColor(120);
      pdf.text(footer, pageWmm / 2, pageHmm - 4, { align: "center" });
      pdf.setTextColor(15);

      offsetY += sliceHpx;
    }
    return fullHmm;
  } finally {
    document.body.removeChild(container);
  }
}

// --- HTML chunks ---
// brandHeader is kept for use inside HTML content sections (e.g. sizing footnote pages).
// For page-level headers use drawPageHeaderBand() / SectionEngine instead.
function brandHeader(logoDataUrl, title, subtitle, projectNumber) {
  return `
    <div style="display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #0f172a;padding-bottom:8px;margin-bottom:12px;">
      ${logoDataUrl ? `<img src="${logoDataUrl}" style="height:38px;" />` : '<div style="font-size:20px;font-weight:800;">ZAMBA</div>'}
      <div style="text-align:right;">
        <div style="font-size:18px;font-weight:700;">${title}${projectNumber ? ` <span style="font-size:11px;color:#475569;font-weight:500;">· No. ${projectNumber}</span>` : ""}</div>
        ${subtitle ? `<div style="font-size:11px;color:#475569;">${subtitle}</div>` : ""}
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Universal page header band — mirrors AppHeader Layout B.
//
// Draws directly onto the current jsPDF page at the top margin:
//   [logo]  [projectName truncated]  …spacer…  [title / subtitle (quiet, right)]
//   ─────── 1.5pt navy rule ──────────────────────────────────────────────────
//
// Returns the Y cursor (mm) immediately BELOW the rule — callers start their
// content there.
//
// headerCtx shape:
//   { logoDataUrl, projectName, title, subtitle }
//   (all fields optional — falls back to text or omits gracefully)
// ---------------------------------------------------------------------------
const HEADER_MARGIN = 8;   // mm from page edge to header content
const HEADER_LOGO_H = 10;  // mm — matches AppHeader h-7 ≈ 28px → ~10mm on A4
const HEADER_BAND_H = HEADER_LOGO_H + 4; // total band height before rule
const HEADER_RULE_OFFSET = HEADER_BAND_H + 1.5; // rule sits 1.5mm below content

function drawPageHeaderBand(pdf, { logoDataUrl, projectName, projectNumber, title, subtitle }) {
  const pageW = pdf.internal.pageSize.getWidth();
  const m = HEADER_MARGIN;
  const bandY = m;

  // --- Logo (left) ---
  if (logoDataUrl) {
    // Keep aspect ratio; logo is rendered at HEADER_LOGO_H tall
    pdf.addImage(logoDataUrl, "PNG", m, bandY + 1, 0, HEADER_LOGO_H, undefined, "FAST");
    // jsPDF addImage with w=0 auto-scales — but not all versions support it.
    // Use a fixed 28mm wide box instead (typical ZAMBA logo aspect ~2.8:1).
    // Overwrite with correct width to avoid zero-dim bugs in some jsPDF builds:
    pdf.addImage(logoDataUrl, "PNG", m, bandY + 1, 28, HEADER_LOGO_H, undefined, "FAST");
  } else {
    pdf.setFontSize(11);
    pdf.setFont(undefined, "bold");
    pdf.setTextColor(15, 23, 42);
    pdf.text("ZAMBA", m, bandY + HEADER_LOGO_H - 1);
  }

  // --- Project name + optional project number (immediately right of logo, truncated) ---
  const logoEndX = m + 30; // 28mm logo + 2mm gap
  const rightCluster = 90;  // mm from left edge where right-cluster starts
  const projectZoneW = pageW - m - rightCluster - logoEndX;

  if (projectName) {
    pdf.setFontSize(10);
    pdf.setFont(undefined, "bold");
    pdf.setTextColor(15, 23, 42);
    // Truncate long names to fit in the zone
    const maxChars = Math.max(10, Math.floor(projectZoneW / 2.2));
    const truncated = projectName.length > maxChars
      ? projectName.slice(0, maxChars - 1) + "…"
      : projectName;
    // When a project number is present, shift the name up slightly to make
    // room for the number subtitle below it.
    const nameY = projectNumber
      ? bandY + HEADER_LOGO_H - 3.5
      : bandY + HEADER_LOGO_H - 1;
    pdf.text(truncated, logoEndX, nameY);

    if (projectNumber) {
      pdf.setFontSize(7);
      pdf.setFont(undefined, "normal");
      pdf.setTextColor(100, 116, 139); // #64748b muted
      pdf.text(String(projectNumber), logoEndX, nameY + 4);
    }
  }

  // --- Title + subtitle cluster (right-aligned) ---
  const rightX = pageW - m;
  if (title) {
    pdf.setFontSize(8);
    pdf.setFont(undefined, "bold");
    pdf.setTextColor(71, 85, 105); // muted-foreground #475569
    pdf.text(title, rightX, bandY + HEADER_LOGO_H - 4, { align: "right" });
  }
  if (subtitle) {
    pdf.setFontSize(7);
    pdf.setFont(undefined, "normal");
    pdf.setTextColor(100, 116, 139); // muted-foreground/70 #64748b
    pdf.text(subtitle, rightX, bandY + HEADER_LOGO_H, { align: "right" });
  }

  // --- 1.5pt navy rule ---
  const ruleY = bandY + HEADER_RULE_OFFSET;
  pdf.setDrawColor(15, 23, 42);
  pdf.setLineWidth(0.53); // 0.53mm ≈ 1.5pt
  pdf.line(m, ruleY, pageW - m, ruleY);

  // Reset text colour
  pdf.setTextColor(15, 23, 42);
  pdf.setFont(undefined, "normal");

  return ruleY + 2; // 2mm breathing room below rule
}

// ---------------------------------------------------------------------------
// Footer helpers — used by the section engine and standalone page draws.
// ---------------------------------------------------------------------------
const FOOTER_ZAMBA_STR = `Generated by ZAMBA Product Selector — www.zambahvac.com`;

function drawPageFooter(pdf, pageNum, totalPages) {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  pdf.setFontSize(6.5);
  pdf.setFont(undefined, "normal");
  pdf.setTextColor(148, 163, 184); // #94a3b8
  pdf.text(FOOTER_ZAMBA_STR, pageW / 2, pageH - 4, { align: "center" });
  if (totalPages != null) {
    pdf.text(`${pageNum} / ${totalPages}`, pageW - HEADER_MARGIN, pageH - 4, { align: "right" });
  }
  pdf.setTextColor(15, 23, 42);
}

// ---------------------------------------------------------------------------
// SectionEngine — section-break engine for PDF generation.
//
// Usage:
//   const eng = new SectionEngine(pdf, headerCtx, { margin: 10 });
//   await eng.startFirstPage();         // draw header on page 1, set cursorY
//   await eng.addHTMLSection(html, opts);
//   await eng.addDrawSection(drawFn);   // drawFn(pdf, startY, availH) → endY
//   eng.stampPageNumbers();             // two-pass: fill in N of M on all pages
//
// Rules:
//   • Every call to addPage() auto-draws the header band.
//   • A section whose rendered height fits on the current page is placed there.
//   • A section that doesn't fit triggers addPage() first.
//   • Sections taller than one full page are split at html2canvas slice boundaries
//     (same as renderHTMLToPdf did before), but with a console.warn.
//   • Cover pages are excluded from the page-number stamp (they have their own
//     visual treatment). Pass { excludeFromPageNumbers: true } to addPage() for cover.
// ---------------------------------------------------------------------------

const _SECTION_ENGINE_PAGE_MARGIN = 10; // default mm
const _SECTION_FOOTER_H = 6;            // mm reserved at bottom for footer

class SectionEngine {
  constructor(pdf, headerCtx, opts = {}) {
    this._pdf = pdf;
    this._headerCtx = headerCtx;
    this._margin = opts.margin ?? _SECTION_ENGINE_PAGE_MARGIN;
    this._pageH = pdf.internal.pageSize.getHeight();
    this._pageW = pdf.internal.pageSize.getWidth();
    this._cursorY = 0;
    // Track pages that should get page-number footers (covers excluded)
    this._numberedPages = []; // 1-based page numbers
    this._isFirstPage = true;
  }

  _contentAvailH() {
    return this._pageH - this._cursorY - this._margin - _SECTION_FOOTER_H;
  }

  // Draw header on the current page, advance cursorY past it.
  _drawHeader() {
    const y = drawPageHeaderBand(this._pdf, this._headerCtx);
    this._cursorY = y;
  }

  // Add a page, draw header, record for page numbering.
  _addPage(orientation) {
    if (orientation) {
      this._pdf.addPage(undefined, orientation);
    } else {
      this._pdf.addPage();
    }
    this._isFirstPage = false;
    this._drawHeader();
    const pageNum = this._pdf.internal.getNumberOfPages();
    this._numberedPages.push(pageNum);
  }

  // Initialise the first page (page 1 already exists after new jsPDF(...)).
  startFirstPage({ coverPage = false } = {}) {
    this._isFirstPage = true;
    if (!coverPage) {
      this._drawHeader();
      const pageNum = this._pdf.internal.getNumberOfPages();
      this._numberedPages.push(pageNum);
    }
  }

  // Add an HTML section. Measures canvas height, decides whether it fits.
  // opts: { antiOrphanSelector, orientation }
  async addHTMLSection(html, opts = {}) {
    // 1. Render to off-screen canvas to measure height
    const { canvas, container } = await _measureHTMLCanvas(html);

    try {
      const m = this._margin;
      const pageW = opts.orientation === "landscape"
        ? this._pdf.internal.pageSize.getHeight() // jsPDF landscape swaps w/h
        : this._pageW;

      // When caller passes an orientation change (e.g. landscape table), we
      // need to add a new page in that orientation before measuring fits.
      if (opts.orientation && opts.orientation !== _currentOrientation(this._pdf)) {
        this._addPage(opts.orientation);
        // Recalculate dimensions for new orientation
        this._pageH = this._pdf.internal.pageSize.getHeight();
        this._pageW = this._pdf.internal.pageSize.getWidth();
      }

      const targetW = this._pageW - m * 2;
      const ratio = targetW / canvas.width;
      const fullHmm = canvas.height * ratio;

      // Collect anti-orphan anchors
      const lineHeightPx = 14 * 1.5;
      const minRunPx = 5 * lineHeightPx;
      const anchors = [];
      if (opts.antiOrphanSelector) {
        container.querySelectorAll(opts.antiOrphanSelector).forEach((el) => {
          const rect = el.getBoundingClientRect();
          const cRect = container.getBoundingClientRect();
          anchors.push((rect.top - cRect.top) * 1.5);
        });
      }

      // Rule: if the section does not fit on the current page, move the WHOLE
      // section to a new page. Never split at an arbitrary cursor offset.
      // Only split across pages when the section is genuinely taller than a
      // full content area (rare — large tables etc.) — in that case we slice
      // in structural chunks rather than abandoning the no-split rule.
      const availH = this._contentAvailH();
      // If section doesn't fit AND there's something already on this page,
      // push to a new page unconditionally (no 35% heuristic).
      // Threshold must be above the post-header cursorY (≈ 25.5mm = 8mm margin
      // + 15.5mm rule offset + 2mm gap).  Using margin+15 (=25mm) was too low —
      // it treated a header-only page as "has content", forcing every section to
      // its own page and inflating page count.  Using margin+18 (=28mm) clears
      // the header baseline with a 2.5mm safety margin.
      const hasContentAbove = this._cursorY > (this._margin + 18); // 28mm > header baseline ~25.5mm
      if (fullHmm > availH && hasContentAbove) {
        this._addPage(undefined);
      }

      // Now slice-render the canvas across pages.
      // For sections that fit on one page (the common case), this loop runs
      // exactly once. For genuinely oversized sections, it splits at the
      // available-height boundary.
      //
      // Parallel fixes to the live-DOM-capture slicing in
      // generateRoomReportPDF (PDF-1 iters 5–7):
      //
      //  1) _addPage must happen BEFORE availHpx is computed, so the new
      //     page's post-header cursorY drives availH. Otherwise iter 2+
      //     reads a stale (near-page-bottom) cursorY and produces
      //     near-zero / negative slices.
      //
      //  2) Slice canvas must be filled with WHITE before drawImage.
      //     JPEG cannot encode alpha — transparent pixels are written
      //     as BLACK. Uncovered slice rows (e.g. when Math.ceil
      //     overshoots the source by 1px, or when sliceHpx underfills)
      //     would otherwise render as black bands in the PDF.
      //
      //  3) Bail BEFORE _addPage when no usable remainder exists, so we
      //     never emit a trailing header-only page on a 1-2 pixel
      //     loop-condition overshoot.
      let offsetY = 0;
      let isFirstSlice = true;
      while (offsetY < canvas.height) {
        const remainingPx = canvas.height - offsetY;
        if (remainingPx < 4) break;

        if (!isFirstSlice) {
          this._addPage(undefined);
        }
        isFirstSlice = false;

        // Compute available-height NOW with the fresh cursorY (post-header
        // on this page).
        const availHpx = (this._contentAvailH()) / ratio;
        let sliceHpx = Math.min(availHpx, remainingPx);

        // Anti-orphan adjustment (subtitle anchors within a single slice)
        const naturalEnd = offsetY + sliceHpx;
        for (const a of anchors) {
          if (a > offsetY + 4 && a < naturalEnd && naturalEnd - a < minRunPx) {
            sliceHpx = Math.max(40, a - offsetY - 4);
            break;
          }
        }

        if (sliceHpx < 4) break;

        const sliceHpxRounded = Math.ceil(sliceHpx);
        const drawHpx = Math.min(sliceHpxRounded, remainingPx);

        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = sliceHpxRounded;
        const sliceCtx = slice.getContext("2d");
        // White fill BEFORE drawImage so uncovered regions don't encode
        // as transparent (which JPEG renders as BLACK).
        sliceCtx.fillStyle = "#ffffff";
        sliceCtx.fillRect(0, 0, slice.width, slice.height);
        sliceCtx.drawImage(
          canvas, 0, offsetY, canvas.width, drawHpx,
          0, 0, canvas.width, drawHpx,
        );
        const sliceImg = slice.toDataURL("image/jpeg", 0.85);
        const sliceHmm = sliceHpxRounded * ratio;

        this._pdf.addImage(
          sliceImg, "JPEG",
          m, this._cursorY,
          targetW, sliceHmm,
          undefined, "FAST"
        );

        this._cursorY += sliceHmm + 1;
        offsetY += drawHpx;

        if (offsetY < canvas.height && sliceHpx < 40) {
          // Safety: section is taller than any single page — we warned, keep going
          // eslint-disable-next-line no-console
          console.warn("[SectionEngine] Section is extremely tall; forced split.");
        }
      }
    } finally {
      document.body.removeChild(container);
    }
  }

  // Add a programmatic draw section.
  // drawFn signature: (pdf, startY, availW, availH) => endY (mm)
  // Returns the new cursorY.
  async addDrawSection(drawFn) {
    // Probe: call with a scratch copy to get height — too expensive for large
    // draws; instead just call it in-place and trust the draw fn to call
    // this._addPage() if it overflows. For the simple case (programmatic table
    // that fits one page), this is fine.
    const endY = await drawFn(
      this._pdf,
      this._cursorY,
      this._pageW - this._margin * 2,
      this._contentAvailH(),
      // addPageFn — draw fn calls this when it overflows
      () => { this._addPage(); return { y: this._cursorY, availH: this._contentAvailH() }; }
    );
    if (endY != null) this._cursorY = endY;
  }

  // Two-pass footer stamp: go through every numbered page and draw "N / M".
  stampPageNumbers() {
    const total = this._numberedPages.length;
    this._numberedPages.forEach((pageNum, idx) => {
      this._pdf.setPage(pageNum);
      drawPageFooter(this._pdf, idx + 1, total);
    });
    // Return to last page
    this._pdf.setPage(this._pdf.internal.getNumberOfPages());
  }
}

// Helper — render HTML to off-screen canvas, return { canvas, container }.
// Caller is responsible for document.body.removeChild(container).
// withLightTheme removes `.dark` from <html> for the duration so dark: Tailwind
// variants never fire on the off-screen container. colorScheme is also forced
// to "light" on the container element itself as a belt-and-suspenders measure.
async function _measureHTMLCanvas(html) {
  const container = document.createElement("div");
  container.style.cssText =
    "position:absolute;left:-9999px;top:0;width:794px;background:#ffffff;color-scheme:light;font-family:system-ui,sans-serif;color:#0f172a;padding:24px;";
  container.innerHTML = html;
  const canvas = await withLightTheme(async () => {
    document.body.appendChild(container);
    // Wait two frames so the browser recalculates computed styles on the
    // newly-attached subtree in light mode before html2canvas walks the DOM.
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
    return html2canvas(container, {
      scale: 1.5,
      useCORS: true,
      backgroundColor: "#ffffff",
      onclone: (doc) => {
        // Belt-and-suspenders: strip dark class from the cloned document too.
        doc.documentElement.classList.remove("dark");
        doc.body.classList.remove("dark");
      },
    });
  });
  return { canvas, container };
}

function _currentOrientation(pdf) {
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  return w > h ? "landscape" : "portrait";
}

// ---------------------------------------------------------------------------
// EngineSelectionSummary HTML serializer.
// Mirrors ModelODetailView.jsx EngineSelectionSummary — no React, no DOM.
// ---------------------------------------------------------------------------
function engineSelectionSummaryHTML(optimalPoint, isSI) {
  if (!optimalPoint) return "";

  const {
    operating_speed,
    target_btuh_basis,
    water_flow_gpm,
    acb_strategy_used,
    actual_pa_cfm,
    nozzle_sp_inwg,
    nozzle_label,
    strategy_minimised,
    strategy_note,
    active_water_cap_source,
    active_water_cap_value_gpm,
    shortfall_btuh,
    split_acb_fraction,
    target_cooling_btuh,
  } = optimalPoint;

  // AHRI 1335 single-basis lock: label is always Q_r.
  // target_btuh_basis from the backend's optimal_point is informational only;
  // display always reads as room-delivered (Q_r, AHRI 1335).
  const basisLabel = target_btuh_basis === "water_side"
    ? "Water-side (Qᴄ)" // defensive fallthrough — should not appear post-lock
    : "Room load (AHRI 1335 — air-side)";

  // Water cap display
  let waterCapDisplay = null;
  if (active_water_cap_source) {
    const gpmStr = active_water_cap_value_gpm != null
      ? `${Number(active_water_cap_value_gpm).toFixed(2)} GPM`
      : null;
    if (active_water_cap_source === "hydraulic") waterCapDisplay = gpmStr ? `Hydraulic ceiling: ${gpmStr}` : "Hydraulic ceiling";
    else if (active_water_cap_source === "max_gpm") waterCapDisplay = gpmStr ? `Max GPM: ${gpmStr}` : "Max GPM cap";
    else if (active_water_cap_source === "gpm_per_ton") waterCapDisplay = gpmStr || "GPM/Ton cap";
    else if (active_water_cap_source === "max_dp") waterCapDisplay = gpmStr ? `Max DP cap → ${gpmStr}` : "Max DP cap";
    else if (active_water_cap_source === "user_water_flow") waterCapDisplay = gpmStr ? `User water flow: ${gpmStr}` : "User water flow";
    else waterCapDisplay = active_water_cap_source;
  }

  // Minimisation chips
  const miniChips = [];
  if (strategy_minimised) {
    if (strategy_minimised.pa_cfm_from != null && strategy_minimised.pa_cfm_to != null) {
      miniChips.push(`PA: ${fmtFlow(strategy_minimised.pa_cfm_from)} → ${fmtFlow(strategy_minimised.pa_cfm_to)} ${uL("flow")}`);
    }
    if (strategy_minimised.sp_from != null && strategy_minimised.sp_to != null) {
      miniChips.push(`SP: ${fmtStatic(strategy_minimised.sp_from)} → ${fmtStatic(strategy_minimised.sp_to)} ${uL("staticPressure")}`);
    }
  }

  // Shortfall
  const hasShortfall = typeof shortfall_btuh === "number" && shortfall_btuh > 0;
  const deliveredBtuh = hasShortfall && target_cooling_btuh != null
    ? Math.round(Number(target_cooling_btuh) - shortfall_btuh)
    : null;

  // O-2 split fraction
  const hasSplit = typeof split_acb_fraction === "number";
  const acbPct = hasSplit ? Math.round(split_acb_fraction * 100 / 5) * 5 : null;
  const opPct  = acbPct != null ? 100 - acbPct : null;

  // ACB strategy labels
  const ACB_STRATEGY_LABELS = {
    given_flow_given_sp: "Given PA flow, given SP",
    given_flow_lowest_sp: "Given PA flow, lowest SP",
    lowest_flow_given_sp: "Lowest PA flow, given SP",
    lowest_flow_lowest_sp: "Lowest PA flow and SP",
  };

  const tile = (label, value) => `
    <div style="padding:4px 6px;">
      <div style="font-size:8px;color:#475569;">${label}</div>
      <div style="font-size:10px;font-weight:600;color:#0f172a;">${value ?? "—"}</div>
    </div>`;

  const tiles = [
    operating_speed ? tile(
      "Operating Speed",
      `<span style="text-transform:capitalize;">${operating_speed} (Optimized)</span>`
    ) : null,
    target_btuh_basis ? tile("Selection Basis", basisLabel) : null,
    water_flow_gpm != null ? tile("Water Flow (modulated)", `${fmtWaterFlow(water_flow_gpm, 2)} ${uL("waterFlow")}`) : null,
    acb_strategy_used ? tile("ACB Strategy", ACB_STRATEGY_LABELS[acb_strategy_used] || acb_strategy_used) : null,
    actual_pa_cfm != null ? tile("PA Flow (actual)", `${fmtFlow(actual_pa_cfm)} ${uL("flow")}`) : null,
    nozzle_sp_inwg != null ? tile("Nozzle SP", `${fmtStatic(nozzle_sp_inwg)} ${uL("staticPressure")}`) : null,
    nozzle_label ? tile("Nozzle Assembly", nozzle_label) : null,
    waterCapDisplay ? tile("Water Flow Cap", waterCapDisplay) : null,
    hasSplit ? tile("Split (Op / ACB)", `${opPct}% / ${acbPct}%`) : null,
  ].filter(Boolean).join("");

  const chipsHtml = miniChips.length ? `
    <div style="display:flex;flex-wrap:wrap;gap:6px;padding:4px 6px;margin-top:4px;">
      <span style="font-size:8px;color:#475569;">Minimised:</span>
      ${miniChips.map((c) => `<span style="font-size:8px;font-weight:600;background:#e0f2fe;color:#075985;padding:1px 6px;border-radius:3px;">${c}</span>`).join("")}
    </div>` : "";

  const shortfallHtml = hasShortfall ? `
    <div style="margin:4px 6px 0;padding:5px 8px;background:#fffbeb;border:1px solid #f59e0b;border-radius:3px;font-size:9px;color:#92400e;">
      <strong>Capacity short</strong> by ${Math.round(shortfall_btuh).toLocaleString()} BTU/h
      ${deliveredBtuh != null && target_cooling_btuh != null
        ? ` (delivered ${deliveredBtuh.toLocaleString()} / target ${Math.round(Number(target_cooling_btuh)).toLocaleString()})`
        : ""}
    </div>` : "";

  const strategyNoteHtml = strategy_note ? `
    <div style="margin:4px 6px 0;padding:5px 8px;background:#fffbeb;border:1px solid #fcd34d;border-radius:3px;font-size:9px;color:#92400e;">${strategy_note}</div>` : "";

  return `
    <div style="margin:8px 0 4px;">
      <div style="font-weight:600;background:#f1f5f9;padding:5px 8px;font-size:10px;border-left:3px solid #0f172a;">Engine Selection Summary</div>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);border:1px solid #e2e8f0;">
        ${tiles}
      </div>
      ${chipsHtml}
      ${shortfallHtml}
      ${strategyNoteHtml}
    </div>
  `;
}

function projectInfoBlock(project, rooms = []) {
  const dc = project.designConditions || {};
  const adv = project.advanced || {};
  // Resolve country label — supports manual "Other" entry.
  const isOtherCountry = dc.country === "__other__";
  const country = isOtherCountry ? (dc.country_other || "") : (dc.country || "");
  const designCity = isOtherCountry
    ? (dc.city_other || "")
    : (dc.city && dc.city !== "__manual__" ? dc.city : "");
  const designCityIsAshrae = !isOtherCountry && designCity;
  // Outdoor + Location are only meaningful when the project includes a
  // Model N / N-f (whose PAC draws outdoor air). For induction / room-air
  // projects we suppress those rows from the PDF.
  const isModelNLike = (m) => m === "Model N" || m === "Model N-f";
  const needsOutdoor = (rooms || []).some((r) => isModelNLike(r.unit_model));
  // Address line shows country only when it's meaningful (i.e. for Model N
  // projects where outdoor design city matters). Otherwise we just keep
  // address-city / state to avoid leaking a default "USA / Anchorage" into
  // documents for room-air-only projects.
  const locationLine =
    [project.cityText, project.state, needsOutdoor ? country : ""].filter(Boolean).join(", ") || "—";
  // ASHRAE design-city row should follow the same visibility rule.
  const showDesignCity = needsOutdoor && designCityIsAshrae;

  // PA EAT comes from Design Conditions (where the user actually enters them),
  // with a legacy fallback to advanced for older projects.
  const paEatDb = dc.pa_eat_db_f ?? adv.pa_eat_db_f;
  const paEatRh = dc.pa_eat_rh ?? adv.pa_eat_rh;

  // Build a compact "Max Length per Model" cell, e.g. "L-L ≤ 3 ft · I-F ≤ 4 ft"
  // (or " ≤ 900 mm" in SI).
  const lenCapByModel = adv.maxLengthByModel || {};
  const lenCapPairs = Object.entries(lenCapByModel)
    .filter(([, v]) => v != null && v !== "")
    .map(([m, v]) => {
      const lbl = _isSI ? `${Math.round(parseFloat(v) * 300)} mm` : `${v} ft`;
      return `${m.replace(/^Model\s*/, "")} ≤ ${lbl}`;
    });
  const maxLenCell = lenCapPairs.length ? lenCapPairs.join(" · ") : "—";

  // PA External Static Pressure (Pa in SI, in. wg in IP).
  const paStaticVal = parseFloat(adv.pa_pressure_in_wg);
  const paStaticCell = Number.isFinite(paStaticVal) && paStaticVal > 0
    ? `${fmtStatic(paStaticVal, _isSI ? 0 : 2)} ${uL("staticPressure")}`
    : "default";

  return `
    <div style="font-size:11px;margin-bottom:10px;">
      <div style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin-bottom:4px;">Project Information</div>
      <table style="width:100%;border-collapse:collapse;">
        <tr><td style="padding:3px;color:#475569;width:25%;vertical-align:middle;">Project</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${project.name || "—"}</td>
            <td style="padding:3px;color:#475569;width:15%;vertical-align:middle;">Project Number</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${project.number || "—"}</td></tr>
        <tr><td style="padding:3px;color:#475569;vertical-align:middle;">Address</td><td style="padding:3px;font-weight:600;vertical-align:middle;" colspan="3">${project.address || "—"}</td></tr>
        <tr><td style="padding:3px;color:#475569;vertical-align:middle;">City / State / Country</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${locationLine}</td>
            <td style="padding:3px;color:#475569;vertical-align:middle;">Date</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${fmtDate()}</td></tr>
        ${showDesignCity ? `<tr><td style="padding:3px;color:#475569;vertical-align:middle;">Design Reference City</td><td style="padding:3px;font-weight:600;vertical-align:middle;" colspan="3">${designCity}${country ? `, ${country}` : ""} (ASHRAE)</td></tr>` : ""}
      </table>

      <div style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin:8px 0 4px;">Design Conditions</div>
      <table style="width:100%;border-collapse:collapse;">
        ${needsOutdoor ? `<tr><td style="padding:3px;color:#475569;width:25%;vertical-align:middle;">Outdoor Summer DB / WB</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${fmtTemp(dc.outdoor_db_f, 1)} / ${fmtTemp(dc.outdoor_wb_f, 1)} ${uL("temp")}</td>
            <td style="padding:3px;color:#475569;vertical-align:middle;">Outdoor Winter DB</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${fmtTemp(dc.outdoor_db_winter_f, 1)} ${uL("temp")}</td></tr>` : ""}
        <tr><td style="padding:3px;color:#475569;width:25%;vertical-align:middle;">Room DB / RH (Summer)</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${fmtTemp(dc.room_db_f, 1)} ${uL("temp")} / ${dc.room_rh || "—"}%</td>
            <td style="padding:3px;color:#475569;vertical-align:middle;">Water EWT (cool/heat)</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${fmtTemp(dc.water_entering_f, 1)} / ${fmtTemp(dc.water_entering_winter_f, 1)} ${uL("temp")}</td></tr>
        ${needsOutdoor ? `<tr><td style="padding:3px;color:#475569;vertical-align:middle;">Altitude</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${fmtLen(dc.altitude_ft, _isSI ? 1 : 0)} ${uL("length")}</td>
            <td style="padding:3px;color:#475569;vertical-align:middle;">Glycol</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${adv.use_glycol ? `${adv.glycol_type} ${adv.glycol_percentage}%` : "None"}</td></tr>` : `<tr><td style="padding:3px;color:#475569;width:25%;vertical-align:middle;">Glycol</td><td style="padding:3px;font-weight:600;vertical-align:middle;" colspan="3">${adv.use_glycol ? `${adv.glycol_type} ${adv.glycol_percentage}%` : "None"}</td></tr>`}
        <tr><td style="padding:3px;color:#475569;vertical-align:middle;">Air-On (Cool / Heat)</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${adv.air_on_temp_f ? `${fmtTemp(adv.air_on_temp_f, 1)} ${uL("temp")}` : "auto (= OA / room)"} / ${adv.air_on_temp_heating_f ? `${fmtTemp(adv.air_on_temp_heating_f, 1)} ${uL("temp")}` : "auto"}</td>
            <td style="padding:3px;color:#475569;vertical-align:middle;">PA EAT DB / RH</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${paEatDb != null && paEatDb !== "" ? `${fmtTemp(paEatDb, 1)} ${uL("temp")}` : "—"} / ${paEatRh != null && paEatRh !== "" ? `${paEatRh}%` : "—"}</td></tr>
        <tr><td style="padding:3px;color:#475569;vertical-align:middle;">PA External Static</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${paStaticCell}</td>
            <td style="padding:3px;color:#475569;vertical-align:middle;">Max Length (per model)</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${maxLenCell}</td></tr>
      </table>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// scheduleTable internals — split into primitives so the large-project path
// can chunk the room rows into batches of SCHEDULE_CHUNK_SIZE, yielding a fresh
// html2canvas call per batch (O(chunk) memory) instead of one giant pass.
// ---------------------------------------------------------------------------

// Number of rooms per html2canvas pass in generateProjectSummaryPDF.
// The schedule renders in A4 LANDSCAPE (210 mm tall), of which ~166 mm is
// usable after header + margins. Each row is ~5–6 mm tall at the compact
// 7.5px font; the section title + table header eat another ~12 mm; the
// last chunk also carries the Project Totals row. 22 data rows per chunk
// keeps a 1.2-page chunk from being sliced mid-row by the engine —
// previously 30 rows fit cleanly in portrait but spilled into 1.4 pages
// after the orientation switch in v20.5.2.
const SCHEDULE_CHUNK_SIZE = 22;

// Per-room aggregation — shared by all schedule rendering paths.
function _roomAgg(r, s) {
  const plan = s.plan || {};
  const qty = plan.qty || 1;
  const units = plan.units && plan.units.length
    ? plan.units
    : [{ qty, per_unit_total: parseFloat(s.cooling_total_btuh) || 0, per_unit_oa: parseFloat(s.oa_cfm) || parseFloat(r.oa_flow_cfm) || 0, result: null }];
  const distinctLengths = new Set(units.map((u) => u.length_ft).filter((l) => l != null));
  const isSplit = units.length > 1 && distinctLengths.size > 1;
  const a = { oa: 0, ea: 0, cool: 0, pa_sens: 0, pa_lat: 0, coil_sens: 0, coil_lat: 0, heat: 0, wf: 0, pd: 0 };
  units.forEach((u) => {
    const sQty = isSplit ? (u.qty || 1) : 1;
    const sub = u.result?.summary || s;
    const perOa = u.per_unit_oa != null
      ? parseFloat(u.per_unit_oa)
      : (parseFloat(sub.oa_cfm) || parseFloat(r.oa_flow_cfm) || 0);
    a.oa += perOa * sQty;
    a.ea += (parseFloat(sub.ea_cfm) || parseFloat(r.ea_flow_cfm) || 0) * sQty;
    a.cool += (parseFloat(u.per_unit_total) || parseFloat(sub.cooling_total_btuh) || 0) * sQty;
    a.pa_sens += (parseFloat(sub.pa_sensible_btuh) || 0) * sQty;
    a.pa_lat += (parseFloat(sub.pa_latent_btuh) || 0) * sQty;
    a.coil_sens += (parseFloat(sub.coil_sensible_btuh) || 0) * sQty;
    a.coil_lat += (parseFloat(sub.coil_latent_btuh) || 0) * sQty;
    a.heat += (parseFloat(sub.heating_btuh) || 0) * sQty;
    a.wf += (parseFloat(sub.water_flow_gpm) || 0) * sQty;
    a.pd = Math.max(a.pd, parseFloat(sub.water_pd_fth2o) || 0);
  });
  return a;
}

// Compact-cell padding for the dense schedule table — 2px vertical + 3px
// horizontal keeps rows on a single line and shaves ~40% of the vertical
// space vs the 5px-all-around used elsewhere. Vlad's 232-room project hit
// 16 pages with the old padding; tighter cells bring it back to ~8 pages.
const _STH = "padding:2px 3px;border:1px solid #0f172a;vertical-align:middle;white-space:nowrap;";
const _STD = "padding:2px 3px;border:1px solid #e2e8f0;vertical-align:middle;white-space:nowrap;";

// Returns the <thead> HTML string for the schedule table.
// hasACBSchedule controls whether the Static Pressure column is included.
function _scheduleTableHead(hasACBSchedule) {
  return `
    <thead style="background:#0f172a;color:#fff;">
      <tr>
        <th style="${_STH}">No</th>
        <th style="${_STH}text-align:left;">Room Name</th>
        <th style="${_STH}text-align:left;">Unit Model</th>
        <th style="${_STH}">Qty</th>
        <th style="${_STH}">OA<br/>(${uL("flow")})</th>
        <th style="${_STH}">EA<br/>(${uL("flow")})</th>
        <th style="${_STH}">Total<br/>(${uL("capacity")})</th>
        <th style="${_STH}">PA Sens<br/>(${uL("capacity")})</th>
        <th style="${_STH}">PA Lat<br/>(${uL("capacity")})</th>
        <th style="${_STH}">Coil Sens<br/>(${uL("capacity")})</th>
        <th style="${_STH}">Coil Lat<br/>(${uL("capacity")})</th>
        <th style="${_STH}">Heating<br/>(${uL("capacity")})</th>
        <th style="${_STH}">Water<br/>(${uL("waterFlow")})</th>
        <th style="${_STH}">PD<br/>(${uL("pressure")})</th>
        ${hasACBSchedule ? `<th style="${_STH}">Static P<br/>(${uL("staticPressure")})</th>` : ""}
        ${hasACBSchedule ? `<th style="${_STH}">NC</th>` : ""}
        ${hasACBSchedule ? `<th style="${_STH}">Air Inlet</th>` : ""}
        <th style="${_STH}">Status</th>
      </tr>
    </thead>`;
}

// Returns <tr> HTML for a slice of rooms [startIdx, endIdx).
// globalIndexOffset is the 1-based display row number for rooms[startIdx].
function _scheduleTableRows(rooms, results, startIdx, endIdx, hasACBSchedule, project = null) {
  const dashCap = (v) => v ? fmtCap(v) : "—";
  return rooms.slice(startIdx, endIdx).map((r, localI) => {
    const i = startIdx + localI;
    const s = results[i]?.summary || {};
    const name = modelLabel(r, results[i]?.raw);
    const plan = s.plan || {};
    const qty = plan.qty || 1;
    const qtyLabel = plan.overridden
      ? `${qty}${plan.units && plan.units.length > 1 ? " (mix)" : ""}`
      : String(qty);
    const ag = _roomAgg(r, s);
    const _spVal = isACB(r.unit_model) ? getRoomStaticPressureInwg(r, results[i], project) : null;
    const spCell = hasACBSchedule
      ? `<td style="${_STD}text-align:right;">${_spVal != null ? fmtStatic(_spVal, _isSI ? 0 : 3) : `<span style="color:#94a3b8;">—</span>`}</td>`
      : "";
    // NC + Air Inlet cells — ACB-only.
    const _isACB = isACB(r.unit_model);
    const _nc = _isACB ? s.nc_level : null;
    const ncCell = hasACBSchedule
      ? `<td style="${_STD}text-align:center;">${(_nc != null) ? Math.round(_nc) : `<span style="color:#94a3b8;">—</span>`}</td>`
      : "";
    const _bore = _isACB
      ? (r.air_inlet_size != null ? r.air_inlet_size : (project?.advanced?.default_acb_spigot_in ?? 6))
      : null;
    const airInletCell = hasACBSchedule
      ? `<td style="${_STD}text-align:center;">${_bore != null ? `${_bore}"` : `<span style="color:#94a3b8;">—</span>`}</td>`
      : "";
    return `<tr>
      <td style="${_STD}">${i + 1}</td>
      <td style="${_STD}">${r.name || `Room ${i + 1}`}</td>
      <td style="${_STD}">${name}</td>
      <td style="${_STD}text-align:center;font-weight:600;">${qtyLabel}</td>
      <td style="${_STD}text-align:right;">${fmtFlow(ag.oa)}</td>
      <td style="${_STD}text-align:right;">${fmtFlow(ag.ea)}</td>
      <td style="${_STD}text-align:right;">${fmtCap(ag.cool)}</td>
      <td style="${_STD}text-align:right;">${dashCap(ag.pa_sens)}</td>
      <td style="${_STD}text-align:right;">${dashCap(ag.pa_lat)}</td>
      <td style="${_STD}text-align:right;">${fmtCap(ag.coil_sens)}</td>
      <td style="${_STD}text-align:right;">${dashCap(ag.coil_lat)}</td>
      <td style="${_STD}text-align:right;">${fmtCap(ag.heat)}</td>
      <td style="${_STD}text-align:right;">${fmtWaterFlow(ag.wf, 2)}</td>
      <td style="${_STD}text-align:right;">${fmtPressure(ag.pd, 2)}</td>
      ${spCell}
      ${ncCell}
      ${airInletCell}
      <td style="${_STD}text-align:center;">${results[i]?.ok ? "OK" : "FAIL"}</td>
    </tr>`;
  }).join("");
}

// Returns the Project Totals <tr> for all rooms.
function _scheduleTableTotals(rooms, results, hasACBSchedule) {
  const t = rooms.reduce((a, r, i) => {
    const s = results[i]?.summary || {};
    const plan = s.plan || {};
    const units = plan.units && plan.units.length
      ? plan.units
      : [{ qty: plan.qty || 1, per_unit_total: parseFloat(s.cooling_total_btuh) || 0, result: null }];
    const isSingleVariant = units.length === 1;
    const singleVariantQty = plan.qty ?? units[0]?.qty ?? 1;
    units.forEach((u) => {
      const subQty = isSingleVariant ? singleVariantQty : (u.qty || 1);
      const subSum = u.result?.summary || s;
      const perOa = u.per_unit_oa != null
        ? parseFloat(u.per_unit_oa)
        : (parseFloat(subSum.oa_cfm) || parseFloat(r.oa_flow_cfm) || 0);
      a.qty += subQty;
      a.oa += perOa * subQty;
      a.ea += (parseFloat(subSum.ea_cfm) || parseFloat(r.ea_flow_cfm) || 0) * subQty;
      a.cool += (parseFloat(subSum.cooling_total_btuh) || parseFloat(u.per_unit_total) || 0) * subQty;
      a.pa_sens += (parseFloat(subSum.pa_sensible_btuh) || 0) * subQty;
      a.pa_lat += (parseFloat(subSum.pa_latent_btuh) || 0) * subQty;
      a.coil_sens += (parseFloat(subSum.coil_sensible_btuh) || 0) * subQty;
      a.coil_lat += (parseFloat(subSum.coil_latent_btuh) || 0) * subQty;
      a.heat += (parseFloat(subSum.heating_btuh) || 0) * subQty;
      a.wf += (parseFloat(subSum.water_flow_gpm) || 0) * subQty;
    });
    return a;
  }, { qty: 0, oa: 0, ea: 0, cool: 0, pa_sens: 0, pa_lat: 0, coil_sens: 0, coil_lat: 0, heat: 0, wf: 0 });

  const dashCap = (v) => v ? fmtCap(v) : "—";
  return `<tr style="background:#f1f5f9;font-weight:600;">
    <td colspan="3" style="${_STD}">Project Totals</td>
    <td style="${_STD}text-align:center;">${t.qty}</td>
    <td style="${_STD}text-align:right;">${fmtFlow(t.oa)}</td>
    <td style="${_STD}text-align:right;">${fmtFlow(t.ea)}</td>
    <td style="${_STD}text-align:right;">${fmtCap(t.cool)}</td>
    <td style="${_STD}text-align:right;">${dashCap(t.pa_sens)}</td>
    <td style="${_STD}text-align:right;">${dashCap(t.pa_lat)}</td>
    <td style="${_STD}text-align:right;">${fmtCap(t.coil_sens)}</td>
    <td style="${_STD}text-align:right;">${dashCap(t.coil_lat)}</td>
    <td style="${_STD}text-align:right;">${fmtCap(t.heat)}</td>
    <td style="${_STD}text-align:right;">${fmtWaterFlow(t.wf, 2)}</td>
    <td style="${_STD}"></td>
    ${hasACBSchedule ? `<td style="${_STD}"></td>` : ""}
    ${hasACBSchedule ? `<td style="${_STD}"></td>` : ""}
    ${hasACBSchedule ? `<td style="${_STD}"></td>` : ""}
    <td style="${_STD}"></td>
  </tr>`;
}

// Full single-blob table — retained for callers that don't need chunking
// (currently only used as the canonical single-pass path for small projects).
function scheduleTable(rooms, results, project = null) {
  const hasACBSchedule = rooms.some((r) => isACB(r.unit_model));
  return `
    <div data-subtitle-anchor style="font-weight:600;background:#f1f5f9;padding:4px 6px;margin-bottom:2px;font-size:10px;">Room Schedule</div>
    <table style="width:100%;border-collapse:collapse;font-size:7.5px;table-layout:auto;">
      ${_scheduleTableHead(hasACBSchedule)}
      <tbody>
        ${_scheduleTableRows(rooms, results, 0, rooms.length, hasACBSchedule, project)}
        ${_scheduleTableTotals(rooms, results, hasACBSchedule)}
      </tbody>
    </table>
  `;
}

// Returns an array of HTML strings — one per chunk — suitable for calling
// eng.addHTMLSection() independently so each batch gets its own html2canvas
// pass.  Every chunk carries the thead so the table is visually self-contained.
// The totals row appears only on the last chunk.
function scheduleTableChunks(rooms, results, chunkSize = SCHEDULE_CHUNK_SIZE, project = null) {
  const hasACBSchedule = rooms.some((r) => isACB(r.unit_model));
  const thead = _scheduleTableHead(hasACBSchedule);
  const chunks = [];
  for (let start = 0; start < rooms.length; start += chunkSize) {
    const end = Math.min(start + chunkSize, rooms.length);
    const isLast = end >= rooms.length;
    const rowsHtml = _scheduleTableRows(rooms, results, start, end, hasACBSchedule, project);
    const totalsHtml = isLast ? _scheduleTableTotals(rooms, results, hasACBSchedule) : "";
    // Only the first chunk carries the section title anchor to avoid the
    // anti-orphan selector triggering a page break between continuation chunks.
    const title = start === 0
      ? `<div data-subtitle-anchor style="font-weight:600;background:#f1f5f9;padding:4px 6px;margin-bottom:2px;font-size:10px;">Room Schedule${rooms.length > chunkSize ? ` (rooms ${start + 1}–${end} of ${rooms.length})` : ""}</div>`
      : `<div style="font-weight:600;background:#f1f5f9;padding:4px 6px;margin-bottom:2px;font-size:10px;">Room Schedule (continued — rooms ${start + 1}–${end} of ${rooms.length})</div>`;
    chunks.push(`
      ${title}
      <table style="width:100%;border-collapse:collapse;font-size:7.5px;table-layout:auto;">
        ${thead}
        <tbody>
          ${rowsHtml}
          ${totalsHtml}
        </tbody>
      </table>
    `);
  }
  return chunks;
}

// Detailed Selection Report — one consolidated table (one row per room) that
// combines the room schedule + per-room technical details + the Configuration
// Variant tag (e.g. EWS-1A) from the Configurator. Used in the Submittal PDF
// in place of the previous separate per-room report pages.
function detailedSelectionReportTable(rooms, results, configurations, project) {
  // 1. Build the (model, length) groups and tag the configurations so each
  //    one has a Configuration Variant tag (EWS-1, EWS-1A, IU-2B, …).
  const groups = buildUnitSchedule(rooms, results);
  const taggedConfigs = withVariantTags(configurations || [], groups);

  // 2. Pre-compute room → ordered list of Configuration Variant tags.
  //    Schema v3: keyed by room.id (stable UUID). Schema v2 fallback: room_idx.
  //    The render loops below resolve the key the same way.
  const tagsByRoomKey = new Map();
  taggedConfigs.forEach((v) => {
    (v.assignments || []).forEach((a) => {
      if ((a.qty || 0) <= 0) return;
      // Prefer room_id (schema v3); fall back to room_idx (schema v2 compat).
      const key = a.room_id != null ? `id:${a.room_id}` : `idx:${a.room_idx}`;
      const arr = tagsByRoomKey.get(key) || [];
      arr.push(v.tag);
      tagsByRoomKey.set(key, arr);
    });
  });

  const dc = project?.designConditions || {};
  const advanced = project?.advanced || {};
  const T_room = parseFloat(dc.room_db_f) || 75;
  const RH_room = parseFloat(dc.room_rh) || 50;
  const T_pa = parseFloat(dc.pa_eat_db_f) || parseFloat(dc.outdoor_db_f) || 95;
  const RH_pa = parseFloat(dc.pa_eat_rh) || 50;
  const T_water_in = parseFloat(dc.water_entering_f) || 45;
  const roomDewpoint = dewpointF(T_room, RH_room);
  // User rule: coil latent = 0 if EWT > (room DP − 1). PA latent is
  // independent of EWT and always reflects PA-vs-room moisture.
  const coilLatentSuppressed = T_water_in > (roomDewpoint - 1);
  // Heating visibility: hide Heating column when no room has heating demand
  // AND no heating water temperature is configured in the project.
  const heatWaterTemp = parseFloat(
    dc.water_entering_heat_f ?? dc.water_entering_winter_f
  );
  const heatingWaterConfigured = Number.isFinite(heatWaterTemp) && heatWaterTemp > 0;
  const anyHeating = (rooms || []).some(
    (r, i) => (parseFloat(results?.[i]?.summary?.heating_btuh) || 0) > 0
  );
  const showHeating = anyHeating || heatingWaterConfigured;

  // Delivered % helper — colour-coded vs target cooling. Falls back to
  // cooling_sensible_btuh when total target is empty (ACB / IU sensible-load).
  // Tolerances: ≥−5% green, −5%..−10% amber, < −10% red.
  //
  // CRITICAL: this MUST mirror the Selection Summary's exact computation
  // (SummaryTab.jsx line ~564–574). The Summary is the source of truth.
  // Multiplier rule:
  //   • DIVIDE mode (default): per-unit values × qty → vs room-level target
  //   • DUPLICATE mode (qty_mode=="duplicate"): per-unit × 1 → vs per-unit target
  //   • SPLIT (mixed lengths): per-unit values already aggregated × 1
  // Caller passes the per-unit delivered total + sensible; multiplier
  // resolution happens inside.
  const deliveredCellForRoom = (room, deliveredTotPerUnit, deliveredSensPerUnit, plan) => {
    const tgtTot = parseFloat(room.cooling_total_btuh) || 0;
    const tgtSen = parseFloat(room.cooling_sensible_btuh) || 0;
    const usingSensible = !(tgtTot > 0) && tgtSen > 0;
    const tgt = tgtTot > 0 ? tgtTot : tgtSen;
    if (!(tgt > 0)) {
      return `<td style="padding:5px;border:1px solid #e2e8f0;text-align:center;color:#94a3b8;vertical-align:middle;">—</td>`;
    }
    const planQty = plan?.qty || 1;
    const planUnits = plan?.units || [];
    const distinctLengths = new Set(planUnits.map((u) => u.length_ft).filter((l) => l != null));
    const isSplit = planUnits.length > 1 && distinctLengths.size > 1;
    const isDuplicate = plan?.qty_mode === "duplicate";
    const mult = (isSplit || isDuplicate) ? 1 : planQty;
    const perUnit = usingSensible ? (deliveredSensPerUnit || 0) : (deliveredTotPerUnit || 0);
    const delivered = perUnit * mult;
    const pct = (delivered / tgt - 1) * 100;
    const color =
      pct >= -5 ? "#059669" :
      pct >= -10 ? "#d97706" :
      "#dc2626";
    const sign = pct > 0 ? "+" : "";
    return `<td style="padding:5px;border:1px solid #e2e8f0;text-align:center;color:${color};font-weight:600;vertical-align:middle;">${sign}${pct.toFixed(1)}%</td>`;
  };

  const paStatic = parseFloat(advanced.pa_pressure_in_wg);
  const paStaticLabel = Number.isFinite(paStatic) && paStatic > 0
    ? fmtStatic(paStatic, _isSI ? 0 : 2)
    : "default";

  // Per-room aggregate (non-PA): PER UNIT — matches SummaryTab. QTY is
  // shown separately; performance columns reflect a single-unit selection
  // regardless of QTY. Mixed-length splits still sum across sub-units.
  const roomAgg = (r, s) => {
    const plan = s.plan || {};
    const qty = plan.qty || 1;
    const units = plan.units && plan.units.length
      ? plan.units
      : [{ qty, per_unit_total: parseFloat(s.cooling_total_btuh) || 0, per_unit_oa: parseFloat(s.oa_cfm) || parseFloat(r.oa_flow_cfm) || 0, result: null }];
    const distinctLengths = new Set(units.map((u) => u.length_ft).filter((l) => l != null));
    const isSplit = units.length > 1 && distinctLengths.size > 1;
    const a = { oa: 0, ea: 0, cool: 0, sens: 0, lat: 0, heat: 0, wf: 0, pd: 0 };
    units.forEach((u) => {
      const sQty = isSplit ? (u.qty || 1) : 1;
      const sub = u.result?.summary || s;
      const perOa = u.per_unit_oa != null
        ? parseFloat(u.per_unit_oa)
        : (parseFloat(sub.oa_cfm) || parseFloat(r.oa_flow_cfm) || 0);
      a.oa += perOa * sQty;
      a.ea += (parseFloat(sub.ea_cfm) || parseFloat(r.ea_flow_cfm) || 0) * sQty;
      a.cool += (parseFloat(sub.cooling_total_btuh) || parseFloat(u.per_unit_total) || 0) * sQty;
      a.sens += (parseFloat(sub.cooling_sensible_btuh) || 0) * sQty;
      a.lat += (parseFloat(sub.cooling_latent_btuh) || 0) * sQty;
      a.heat += (parseFloat(sub.heating_btuh) || 0) * sQty;
      a.wf += (parseFloat(sub.water_flow_gpm) || 0) * sQty;
      a.pd = Math.max(a.pd, parseFloat(sub.water_pd_fth2o) || 0);
    });
    return a;
  };

  // Per-room aggregate for PA models — PER UNIT (matches SummaryTab).
  // QTY column is shown separately; capacities reflect a single unit.
  const paAgg = (r, s, raw) => {
    const speedEntry = (raw?.results?.[0]?.cooling?.speeds || []).find((sp) => sp.speed === "pa") || {};
    const paCfm = parseFloat(speedEntry.primary_cfm) || parseFloat(s.primary_air_cfm) || parseFloat(r.oa_flow_cfm) || 0;
    const qty = (s.plan?.qty) || 1;
    const coilSens = parseFloat(speedEntry.coil_sensible_btuh) || parseFloat(s.cooling_sensible_btuh) || 0;
    const coilLat = coilLatentSuppressed ? 0 : (parseFloat(speedEntry.coil_latent_btuh) || parseFloat(s.cooling_latent_btuh) || 0);
    // Apply Model D derate to PA psychro contribution (coil fields are already
    // derated by the backend's _scale_model_d_capacities walker).
    const paDerate = r?.unit_model === "Model D" ? MODEL_D_CAPACITY_DERATE : 1.0;
    const paSens = paSensibleCapacity(paCfm, T_room, T_pa) * paDerate;
    const paLat = paLatentCapacity(paCfm, T_room, RH_room, T_pa, RH_pa) * paDerate;
    return {
      qty,
      paCfm,
      paSens,
      paLat,
      coilSens,
      coilLat,
      totalSens: paSens + coilSens,
      totalLat: paLat + coilLat,
      lwt: speedEntry.leaving_water_f,
      gpm: parseFloat(speedEntry.water_flow_gpm) || parseFloat(s.water_flow_gpm) || 0,
      pd: parseFloat(speedEntry.water_pd_fth2o) || parseFloat(s.water_pd_fth2o) || 0,
      nozzle: speedEntry.nozzle_label || speedEntry.nozzle || "—",
    };
  };

  // Split rooms into two buckets.
  const paIdx = [];
  const stdIdx = [];
  rooms.forEach((r, i) => {
    if (isPrimaryAir(r.unit_model)) paIdx.push(i);
    else stdIdx.push(i);
  });

  // -------- Standard (non-PA) sub-table --------
  let stdHtml = "";
  if (stdIdx.length) {
    const stdRows = stdIdx.map((i) => {
      const r = rooms[i];
      const s = results[i]?.summary || {};
      const name = modelLabel(r, results[i]?.raw);
      const plan = s.plan || {};
      const qty = plan.qty || 1;
      const qtyLabel = plan.overridden
        ? `${qty}${plan.units && plan.units.length > 1 ? " (mix)" : ""}`
        : String(qty);
      const ag = roomAgg(r, s);
      // Prefer room.id (schema v3); fall back to positional index (schema v2 compat).
      const roomTagKey = r.id != null ? `id:${r.id}` : `idx:${i}`;
      const tags = tagsByRoomKey.get(roomTagKey) || [];
      const variantCell = tags.length
        ? `<span style="font-weight:700;color:#0f172a;">${tags.join(", ")}</span>`
        : `<span style="color:#94a3b8;">—</span>`;
      return `<tr>
        <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;">${i + 1}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;">${r.name || `Room ${i + 1}`}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;">${name}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;vertical-align:middle;">${variantCell}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;font-weight:600;vertical-align:middle;">${qtyLabel}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtFlow(ag.oa)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtFlow(ag.ea)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(ag.cool)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(ag.sens)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(ag.lat)}</td>
        ${showHeating ? `<td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(ag.heat)}</td>` : ""}
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;font-weight:600;vertical-align:middle;">${fmtWaterFlow(ag.wf, 2)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtPressure(ag.pd, 2)}</td>
        ${results[i]?.ok
          ? deliveredCellForRoom(r, ag.cool, ag.sens, plan)
          : `<td style="padding:5px;border:1px solid #e2e8f0;text-align:center;color:#dc2626;font-weight:600;vertical-align:middle;">FAIL</td>`}
      </tr>`;
    }).join("");

    const totals = stdIdx.reduce((acc, i) => {
      const r = rooms[i], s = results[i]?.summary || {};
      const ag = roomAgg(r, s);
      const q = (s.plan?.qty) || 1;
      // Per-row aggregates are PER UNIT; multiply by qty for project subtotals.
      acc.qty += q;
      acc.oa += ag.oa * q; acc.ea += ag.ea * q; acc.cool += ag.cool * q;
      acc.sens += ag.sens * q; acc.lat += ag.lat * q; acc.heat += ag.heat * q;
      acc.wf += ag.wf * q;
      return acc;
    }, { qty: 0, oa: 0, ea: 0, cool: 0, sens: 0, lat: 0, heat: 0, wf: 0 });

    stdHtml = `
      <table style="width:100%;border-collapse:collapse;font-size:9px;margin-bottom:10px;">
        <thead style="background:#0f172a;color:#fff;">
          <tr>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">No</th>
            <th style="padding:5px;border:1px solid #0f172a;text-align:left;vertical-align:middle;">Room Name</th>
            <th style="padding:5px;border:1px solid #0f172a;text-align:left;vertical-align:middle;">Unit Model</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Variant</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Qty</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">OA<br/>(${uL("flow")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">EA<br/>(${uL("flow")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Total<br/>(${uL("capacity")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Sens.<br/>(${uL("capacity")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Latent<br/>(${uL("capacity")})</th>
            ${showHeating ? `<th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Heating<br/>(${uL("capacity")})</th>` : ""}
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Water<br/>(${uL("waterFlow")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">PD<br/>(${uL("pressure")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;" title="Delivered total cooling vs required">Delivered</th>
          </tr>
        </thead>
        <tbody>
          ${stdRows}
          <tr style="background:#f1f5f9;font-weight:600;">
            <td colspan="4" style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;">Subtotal</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;vertical-align:middle;">${totals.qty}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtFlow(totals.oa)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtFlow(totals.ea)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(totals.cool)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(totals.sens)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(totals.lat)}</td>
            ${showHeating ? `<td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(totals.heat)}</td>` : ""}
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtWaterFlow(totals.wf, 2)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;"></td>
            <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;"></td>
          </tr>
        </tbody>
      </table>
    `;
  }

  // -------- Primary-Air sub-table --------
  let paHtml = "";
  if (paIdx.length) {
    const paRows = paIdx.map((i) => {
      const r = rooms[i];
      const s = results[i]?.summary || {};
      const name = modelLabel(r, results[i]?.raw);
      const plan = s.plan || {};
      const qty = plan.qty || 1;
      const ag = paAgg(r, s, results[i]?.raw);
      // Prefer room.id (schema v3); fall back to positional index (schema v2 compat).
      const roomTagKey = r.id != null ? `id:${r.id}` : `idx:${i}`;
      const tags = tagsByRoomKey.get(roomTagKey) || [];
      const variantCell = tags.length
        ? `<span style="font-weight:700;color:#0f172a;">${tags.join(", ")}</span>`
        : `<span style="color:#94a3b8;">—</span>`;
      return `<tr>
        <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;">${i + 1}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;">${r.name || `Room ${i + 1}`}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;">${name}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;vertical-align:middle;">${variantCell}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;font-weight:600;vertical-align:middle;">${qty}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;font-weight:600;vertical-align:middle;">${fmtFlow(ag.paCfm)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;font-size:8px;color:#475569;vertical-align:middle;">${ag.nozzle}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${(() => { const _sp = getRoomStaticPressureInwg(r, results[i], project); return _sp != null ? fmtStatic(_sp, _isSI ? 0 : 3) : `<span style="color:#94a3b8;">—</span>`; })()}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;vertical-align:middle;">${(s.nc_level != null) ? Math.round(s.nc_level) : `<span style="color:#94a3b8;">—</span>`}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;vertical-align:middle;">${(r.air_inlet_size != null ? r.air_inlet_size : (project?.advanced?.default_acb_spigot_in ?? 6))}&quot;</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(ag.paSens)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(ag.paLat)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(ag.coilSens)}</td>
        ${!coilLatentSuppressed ? `<td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(ag.coilLat)}</td>` : ""}
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;font-weight:600;vertical-align:middle;">${fmtCap(ag.totalSens)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;font-weight:600;vertical-align:middle;">${fmtCap(ag.totalLat)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${ag.lwt != null ? fmtTemp(ag.lwt) : "—"}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;font-weight:600;vertical-align:middle;">${fmtWaterFlow(ag.gpm, 2)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtPressure(ag.pd, 2)}</td>
        ${results[i]?.ok
          ? deliveredCellForRoom(r, (ag.totalSens || 0) + (ag.totalLat || 0), ag.totalSens || 0, plan)
          : `<td style="padding:5px;border:1px solid #e2e8f0;text-align:center;color:#dc2626;font-weight:600;vertical-align:middle;">FAIL</td>`}
      </tr>`;
    }).join("");

    const totals = paIdx.reduce((acc, i) => {
      const r = rooms[i], s = results[i]?.summary || {};
      const ag = paAgg(r, s, results[i]?.raw);
      const q = (s.plan?.qty) || 1;
      // Per-row aggregates are PER UNIT; multiply by qty for project subtotals.
      acc.qty += q;
      acc.paCfm += ag.paCfm * q;
      acc.paSens += ag.paSens * q; acc.paLat += ag.paLat * q;
      acc.coilSens += ag.coilSens * q; acc.coilLat += ag.coilLat * q;
      acc.totalSens += ag.totalSens * q; acc.totalLat += ag.totalLat * q;
      acc.gpm += ag.gpm * q;
      return acc;
    }, { qty: 0, paCfm: 0, paSens: 0, paLat: 0, coilSens: 0, coilLat: 0, totalSens: 0, totalLat: 0, gpm: 0 });

    paHtml = `
      <table style="width:100%;border-collapse:collapse;font-size:9px;margin-top:${stdIdx.length ? 14 : 0}px;">
        <thead style="background:#0f172a;color:#fff;">
          <tr>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">No</th>
            <th style="padding:5px;border:1px solid #0f172a;text-align:left;vertical-align:middle;">Room Name</th>
            <th style="padding:5px;border:1px solid #0f172a;text-align:left;vertical-align:middle;">Unit Model</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Variant</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Qty</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">PA Flow<br/>(${uL("flow")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Nozzle</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Static<br/>(${uL("staticPressure")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;" title="Noise Criterion (driven by SP, primary CFM, and spigot bore for C/C1/D)">NC</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;" title="Spigot / air-inlet bore (inches)">Air Inlet</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">PA Sens<br/>(${uL("capacity")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">PA Lat<br/>(${uL("capacity")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Coil Sens<br/>(${uL("capacity")})</th>
            ${!coilLatentSuppressed ? `<th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Coil Lat<br/>(${uL("capacity")})</th>` : ""}
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Total Sens<br/>(${uL("capacity")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Total Lat<br/>(${uL("capacity")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">LWT<br/>(${uL("temp")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Water<br/>(${uL("waterFlow")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">PD<br/>(${uL("pressure")})</th>
            <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;" title="Delivered total cooling vs required">Delivered</th>
          </tr>
        </thead>
        <tbody>
          ${paRows}
          <tr style="background:#f1f5f9;font-weight:600;">
            <td colspan="4" style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;">Subtotal</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;vertical-align:middle;">${totals.qty}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtFlow(totals.paCfm)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;"></td>
            <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;"></td>
            <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;"></td>
            <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;"></td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(totals.paSens)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(totals.paLat)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(totals.coilSens)}</td>
            ${!coilLatentSuppressed ? `<td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(totals.coilLat)}</td>` : ""}
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(totals.totalSens)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(totals.totalLat)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;"></td>
            <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtWaterFlow(totals.gpm, 2)}</td>
            <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;"></td>
            <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;"></td>
          </tr>
        </tbody>
      </table>
    `;
  }

  return `
    <div data-subtitle-anchor style="font-weight:600;background:#0f172a;color:#fff;padding:6px 10px;margin-bottom:4px;font-size:11px;">
      Detailed Selection Report
    </div>
    ${stdHtml}
    ${paHtml}
  `;
}

// Unit Schedule: groups identical (model + length) units across all rooms.
function unitScheduleTable(rooms, results) {
  const groups = buildUnitSchedule(rooms, results);
  if (!groups.length) return "";

  const rowsHtml = groups
    .map((g) => {
      const u = g.per_unit;
      // Models N / N-f have a single physical size — don't show a length
      const isLengthAgnostic = ["Model N", "Model N-f"].includes(g.unit_model);
      const lengthLabel = isLengthAgnostic
        ? "—"
        : (g.length_ft
          ? (_isSI
            ? `${Math.round(parseFloat(g.length_ft) * 300)} mm`
            : `${fmtLen(g.length_ft)} ${uL("length")}`)
          : "—");
      const circuitsCell = (!isLengthAgnostic && g.circuits != null) ? `${g.circuits} ckt` : "—";
      return `<tr>
        <td style="padding:5px;border:1px solid #e2e8f0;font-weight:600;vertical-align:middle;">${g.tag}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;">${g.unit_model}${g.length_ft && !isLengthAgnostic ? `-${_isSI ? Math.round(parseFloat(g.length_ft) * 300) : g.length_ft}` : ""}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;vertical-align:middle;">${lengthLabel}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;vertical-align:middle;">${circuitsCell}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;font-weight:600;vertical-align:middle;">${g.qty}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtFlow(u.oa_cfm)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(u.total_btuh)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(u.sensible_btuh)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(u.latent_btuh)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtCap(u.heating_btuh)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtWaterFlow(u.water_flow_gpm, 2)}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:right;vertical-align:middle;">${fmtPressure(u.water_pd_fth2o, 2)}</td>
      </tr>`;
    })
    .join("");

  const totalQty = groups.reduce((s, g) => s + g.qty, 0);

  return `
    <div data-subtitle-anchor style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin:14px 0 4px;font-size:11px;">Unit Schedule (Per-Unit Performance)</div>
    <table style="width:100%;border-collapse:collapse;font-size:9px;">
      <thead style="background:#0f172a;color:#fff;">
        <tr>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Tag</th>
          <th style="padding:5px;border:1px solid #0f172a;text-align:left;vertical-align:middle;">Unit Model</th>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Length</th>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Circuits</th>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Qty</th>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">OA / unit<br/>(${uL("flow")})</th>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Total / unit<br/>(${uL("capacity")})</th>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Sens. / unit<br/>(${uL("capacity")})</th>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Lat. / unit<br/>(${uL("capacity")})</th>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Heat. / unit<br/>(${uL("capacity")})</th>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">Water / unit<br/>(${uL("waterFlow")})</th>
          <th style="padding:5px;border:1px solid #0f172a;vertical-align:middle;">PD / unit<br/>(${uL("pressure")})</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
        <tr style="background:#f1f5f9;font-weight:600;">
          <td colspan="4" style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;">Total Units</td>
          <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;vertical-align:middle;">${totalQty}</td>
          <td colspan="7" style="padding:5px;border:1px solid #e2e8f0;vertical-align:middle;"></td>
        </tr>
      </tbody>
    </table>
  `;
}

function roomReportBody(room, result) {
  if (!result?.ok) {
    return `<div style="padding:12px;background:#fef2f2;color:#b91c1c;border:1px solid #fecaca;border-radius:4px;font-size:11px;">
      Selection failed for ${room.name}: ${result?.error || "unknown error"}
    </div>`;
  }
  const raw = result.raw;
  const isN = ["Model N", "Model N-f"].includes(room.unit_model);

  // Inputs section
  const selCircuits = result.summary?.selected_circuits ?? null;
  const circuitLabel = selCircuits != null ? ` — ${selCircuits} ckt` : "";
  const inputs = `
    <div style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin:8px 0 4px;font-size:11px;">Room Inputs</div>
    <table style="width:100%;border-collapse:collapse;font-size:10px;">
      <tr>
        <td style="padding:3px;color:#475569;width:18%;vertical-align:middle;">Unit Model</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${modelLabel(room, raw)}${circuitLabel}</td>
        <td style="padding:3px;color:#475569;width:15%;vertical-align:middle;">OA / EA Flow</td><td style="padding:3px;font-weight:600;vertical-align:middle;">${fmtFlow(room.oa_flow_cfm)} / ${fmtFlow(room.ea_flow_cfm)} ${uL("flow")}</td>
      </tr>
      <tr>
        <td style="padding:3px;color:#475569;vertical-align:middle;">Target Total / Sens. / Lat.</td>
        <td style="padding:3px;font-weight:600;vertical-align:middle;">${fmtCap(room.cooling_total_btuh)} / ${fmtCap(room.cooling_sensible_btuh)} / ${fmtCap(room.cooling_latent_btuh)} ${uL("capacity")}</td>
        <td style="padding:3px;color:#475569;vertical-align:middle;">Heating Demand</td>
        <td style="padding:3px;font-weight:600;vertical-align:middle;">${fmtCap(room.heating_btuh)} ${uL("capacity")}</td>
      </tr>
    </table>
  `;

  // Capacity tables
  let capacity = "";
  if (isN) {
    const ai = raw?.summary_table?.air_side || {};
    const ws = raw?.summary_table?.water_side || {};
    capacity = `
      <div style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin:8px 0 4px;font-size:11px;">Air-Side Delivered</div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <thead style="background:#e2e8f0;">
          <tr>
            <th style="padding:5px;border:1px solid #cbd5e1;text-align:left;vertical-align:middle;">Stream</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">${uL("flow")}</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">Sensible (${uL("capacity")})</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">Latent (${uL("capacity")})</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">Total (${uL("capacity")})</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding:5px;border:1px solid #cbd5e1;font-weight:600;vertical-align:middle;">PAC</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtFlow(ai.pac_airflow_cfm)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap(ai.pac_sensible_btuh)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap(ai.pac_latent_btuh)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap((ai.pac_sensible_btuh || 0) + (ai.pac_latent_btuh || 0))}</td>
          </tr>
          <tr>
            <td style="padding:5px;border:1px solid #cbd5e1;font-weight:600;vertical-align:middle;">Spinnaker</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtFlow(ai.spinnaker_airflow_cfm)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap(ai.spinnaker_sensible_btuh)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap(ai.spinnaker_latent_btuh)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap((ai.spinnaker_sensible_btuh || 0) + (ai.spinnaker_latent_btuh || 0))}</td>
          </tr>
          <tr style="background:#f1f5f9;font-weight:600;">
            <td style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">System</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtFlow(ai.total_airflow_cfm)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap(ai.sensible_capacity_btuh)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap(ai.latent_capacity_btuh)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap(ai.total_capacity_btuh)}</td>
          </tr>
        </tbody>
      </table>

      <div style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin:8px 0 4px;font-size:11px;">Water-Side Summary</div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <thead style="background:#e2e8f0;">
          <tr>
            <th style="padding:5px;border:1px solid #cbd5e1;text-align:left;vertical-align:middle;">Coil</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">Flow (${uL("waterFlow")})</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">EWT (${uL("temp")})</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">LWT (${uL("temp")})</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">PD (${uL("pressure")})</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">Capacity (${uL("capacity")})</th>
          </tr>
        </thead>
        <tbody>
          ${["pac", "spinnaker"].map((k) => {
            const c = ws[k] || {};
            return `<tr>
              <td style="padding:5px;border:1px solid #cbd5e1;font-weight:600;text-transform:capitalize;vertical-align:middle;">${k}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtWaterFlow(c.water_flow_gpm, 2)}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtTemp(c.water_entering_f, 1)}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtTemp(c.water_leaving_f, 1)}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtPressure(c.water_pd_fth2o, 2)}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap(c.capacity_btuh)}</td>
            </tr>`;
          }).join("")}
          <tr style="background:#f1f5f9;font-weight:600;">
            <td style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">Total</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtWaterFlow(ws.total_water_flow_gpm, 2)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;" colspan="2"></td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtPressure(ws.total_water_pd_fth2o, 2)}</td>
            <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtNum(ws.total_chiller_capacity_btuh)}</td>
          </tr>
        </tbody>
      </table>
    `;
  } else {
    const best = raw?.results?.[0] || {};
    const speeds = best?.cooling?.speeds || [];
    capacity = `
      <div style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin:8px 0 4px;font-size:11px;">Cooling — per fan speed</div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <thead style="background:#e2e8f0;">
          <tr>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">Speed</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">${uL("flow")}</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">Sens.</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">Latent</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">Total (${uL("capacity")})</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">${uL("waterFlow")}</th>
            <th style="padding:5px;border:1px solid #cbd5e1;vertical-align:middle;">PD (${uL("pressure")})</th>
          </tr>
        </thead>
        <tbody>
          ${speeds.map((s) => {
            const a = s.air_side || {};
            return `<tr>
              <td style="padding:5px;border:1px solid #cbd5e1;text-transform:capitalize;font-weight:600;vertical-align:middle;">${s.speed}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtFlow(s.cfm)}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap(a.air_sensible_btuh ?? s.final_sensible_btuh)}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtCap(a.air_latent_btuh ?? s.final_latent_btuh)}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;font-weight:600;vertical-align:middle;">${fmtCap(a.air_total_btuh ?? s.final_total_btuh)}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtWaterFlow(s.water_flow_gpm, 2)}</td>
              <td style="padding:5px;border:1px solid #cbd5e1;text-align:right;vertical-align:middle;">${fmtPressure(s.water_pd_fth2o, 2)}</td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>
    `;
  }

  return inputs + capacity;
}

// ---------------------------------------------------------------------------
// Sizing footnote pages — one page per product family present in the project.
// Returns an array of { familyLabel, bodyHtml } objects. The caller is
// responsible for adding a new page + header; this function returns only the
// body content (no brandHeader wrapper — that would double-draw the header
// when called inside SectionEngine).
//
// Family taxonomy:
//   FACB  — Model S, S-HW, O-1, O-2  (fan-assisted active chilled beam)
//   ACB   — Model C, C1, H, I-*, L-* (passive induction / primary air)
//   EWS   — Model N, N-f              (ERV-based system)
// ---------------------------------------------------------------------------
function buildSizingFootnotePages(logo, project, rooms) {
  const allModels = (rooms || []).map((r) => r?.unit_model).filter(Boolean);

  const makeBody = (label, familyRooms) => `
    <div style="font-size:12px;line-height:1.6;color:#0f172a;padding:16px 0;">
      <div style="font-weight:700;font-size:14px;margin-bottom:8px;">Sizing Footnote — ${label}</div>
      <p style="margin:0;">${formatSizingFootnote(project, familyRooms)}</p>
    </div>
  `;

  const families = [];
  if (allModels.some(isFACB)) {
    const familyRooms = rooms.filter((r) => r?.unit_model && isFACB(r.unit_model));
    families.push({
      familyLabel: "FACB / Model O — Fan-Assisted Active Chilled Beam",
      bodyHtml: makeBody("FACB Family", familyRooms),
    });
  }
  if (allModels.some(isACB)) {
    const familyRooms = rooms.filter((r) => r?.unit_model && isACB(r.unit_model));
    families.push({
      familyLabel: "ACB / PA — Primary Air (Induction) Models",
      bodyHtml: makeBody("ACB / Primary Air Family", familyRooms),
    });
  }
  if (allModels.some(isEWS)) {
    const familyRooms = rooms.filter((r) => r?.unit_model && isEWS(r.unit_model));
    families.push({
      familyLabel: "EWS — ERV-Based System (Model N / N-f)",
      bodyHtml: makeBody("EWS Family", familyRooms),
    });
  }
  return families;
}

// ---------------------------------------------------------------------------
// captureLiveSourceAndAddPages — shared live-DOM capture pipeline.
//
// Captures `sourceEl` via html2canvas (inside withLightTheme), measures
// section boundaries and content bottom from the CLONE (so measurements
// align with the rendered canvas), then slices the result across PDF pages
// using `eng` for page management.
//
// All PDF-1 fixes are applied:
//   • onclone strips dark, hides [data-pdf-inner-brand] + [data-pdf-hide-in-capture]
//   • section boundaries measured from clone, not live DOM
//   • _addPage() called BEFORE availHpx computed (so cursorY is post-header)
//   • white fill on every slice canvas before drawImage
//   • trailing-empty-page guard (remainingPx < 4 bail)
//   • section-aware snap (no mid-section page breaks)
//
// Does NOT save the PDF or call stampPageNumbers — the caller's responsibility.
// ---------------------------------------------------------------------------
async function captureLiveSourceAndAddPages(pdf, eng, sourceEl) {
  let cloneSectionBoundaries = [];
  let cloneContentBottom = 0;
  const captureScale = 1.5;
  const canvas = await withLightTheme(async () => {
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
    return html2canvas(sourceEl, {
      scale: captureScale,
      useCORS: true,
      backgroundColor: "#ffffff",
      onclone: (doc, clonedEl) => {
        doc.documentElement.classList.remove("dark");
        doc.body.classList.remove("dark");
        doc.querySelectorAll(".print\\:hidden").forEach((el) => {
          el.style.display = "none";
        });
        doc.querySelectorAll("[data-pdf-inner-brand]").forEach((el) => {
          el.style.display = "none";
        });
        // Hide elements explicitly marked to skip the PDF capture
        // (e.g. the live-tree's own "Generated by ZAMBA..." footer,
        // which the universal page footer already provides).
        doc.querySelectorAll("[data-pdf-hide-in-capture]").forEach((el) => {
          el.style.display = "none";
        });

        // Measure section positions in the cloned DOM AFTER applying the
        // display:none mutations above. The cloned doc's layout is what
        // html2canvas captures, so these measurements align with the
        // canvas pixel coordinates exactly.
        //
        // html2canvas passes the cloned target element as the second arg
        // when scale is set. Fall back to querySelector if not available.
        const target = clonedEl
          ? clonedEl
          : doc.querySelector("[ref-source-el]") || doc.body;
        const containerRect = target.getBoundingClientRect();
        target.querySelectorAll("[data-pdf-section]").forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.width === 0 || r.height === 0) return;
          cloneSectionBoundaries.push({
            top: Math.floor((r.top - containerRect.top) * captureScale),
            bottom: Math.ceil((r.bottom - containerRect.top) * captureScale),
          });
        });
        cloneSectionBoundaries.sort((a, b) => a.top - b.top);

        // Content bottom from the clone too.
        let maxBottom = 0;
        target.querySelectorAll("*").forEach((el) => {
          if (el.hasAttribute("data-pdf-inner-brand")) return;
          const r = el.getBoundingClientRect();
          if (r.width === 0 || r.height === 0) return;
          const relBottom = r.bottom - containerRect.top;
          if (relBottom > maxBottom) maxBottom = relBottom;
        });
        cloneContentBottom = Math.ceil(maxBottom * captureScale);
      },
    });
  });

  const m = 10;
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const targetW = pageW - m * 2;
  const ratio = targetW / canvas.width;

  const sectionBoundaries = cloneSectionBoundaries;
  let effectiveCanvasH = canvas.height;
  if (cloneContentBottom > 0) {
    effectiveCanvasH = Math.min(canvas.height, cloneContentBottom + 12);
  }

  // Slice the canvas across pages, with header on each new page.
  //
  // Bug fixes applied (PDF-1 iters 5–11):
  //  1) _addPage() BEFORE availHpx so cursorY is fresh (post-header).
  //  2) White fill on slice canvas before drawImage (JPEG + alpha = black).
  //  3) Bail before _addPage when remainingPx < 4 (no trailing empty page).
  //  4) Section-aware snap: don't cut mid-section.
  let offsetY = 0;
  let isFirstSlice = true;
  while (offsetY < effectiveCanvasH) {
    // Bail BEFORE _addPage so we never emit a trailing empty page.
    const remainingPx = effectiveCanvasH - offsetY;
    if (remainingPx < 4) break;

    if (!isFirstSlice) {
      eng._addPage();
    }
    isFirstSlice = false;

    // Compute available height with the CURRENT cursorY (post-header on
    // this page). Must happen AFTER _addPage.
    const availH = pageH - eng._cursorY - m - _SECTION_FOOTER_H;
    const availHpx = availH / ratio;
    let sliceHpx = Math.min(availHpx, remainingPx);

    // Section-aware snap: prevent cutting a section in half.
    const naturalEnd = offsetY + sliceHpx;
    const fullPageHpx = (pageH - m - m - 15 - _SECTION_FOOTER_H) / ratio;
    let bestSnap = null;
    let mustSnap = false;
    for (let i = 0; i < sectionBoundaries.length; i++) {
      const sec = sectionBoundaries[i];
      if (sec.bottom <= offsetY + 4) continue;
      if (sec.top >= naturalEnd) break;
      if (sec.bottom > naturalEnd) {
        if (sec.bottom - sec.top < fullPageHpx) {
          mustSnap = true;
        }
        break;
      }
      bestSnap = sec.bottom;
    }
    if (mustSnap && bestSnap !== null) {
      const snapped = bestSnap - offsetY + 4;
      if (snapped >= 40 && snapped < sliceHpx) {
        sliceHpx = snapped;
      }
    }

    if (sliceHpx < 4) break;

    const sliceHpxRounded = Math.ceil(sliceHpx);
    const drawHpx = Math.min(sliceHpxRounded, effectiveCanvasH - offsetY);

    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = sliceHpxRounded;
    const sliceCtx = slice.getContext("2d");
    // White fill first — JPEG encodes transparent pixels as black.
    sliceCtx.fillStyle = "#ffffff";
    sliceCtx.fillRect(0, 0, slice.width, slice.height);
    sliceCtx.drawImage(canvas, 0, offsetY, canvas.width, drawHpx, 0, 0, canvas.width, drawHpx);

    pdf.addImage(slice.toDataURL("image/jpeg", 0.85), "JPEG", m, eng._cursorY, targetW, sliceHpxRounded * ratio, undefined, "FAST");
    eng._cursorY += sliceHpxRounded * ratio + 1;
    offsetY += drawHpx;
  }
}

// ----------- Public API -----------

// Per-room PDF.
//
// When sourceEl is available (live RoomReportPage in DOM), it captures the
// psychrometric chart and interactive content via html2canvas — but now wraps
// it in the universal header band on every page.
//
// When sourceEl is absent (background rendering path), it builds each content
// section programmatically so the EngineSelectionSummary is always present.
export async function generateRoomReportPDF({ project, room, result, displayName, sourceEl, isSI = false }) {
  _isSI = isSI;
  // Outer dark-mode lock: strip `.dark` for the entire PDF generation so that
  // live DOM elements, off-screen containers, and html2canvas clones all resolve
  // CSS custom properties against the light theme.  Restored in `finally` even
  // on error.  The per-capture withLightTheme calls are kept as defence-in-depth.
  const htmlEl = document.documentElement;
  const wasDark = htmlEl.classList.contains("dark");
  if (wasDark) htmlEl.classList.remove("dark");
  await new Promise((r) => requestAnimationFrame(r));
  await new Promise((r) => requestAnimationFrame(r));
  try {
  const logo = await preloadLogo();
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const headerCtx = {
    logoDataUrl: logo,
    projectName: project?.name || room?.name || "Room Report",
    projectNumber: project?.number || undefined,
    title: room?.name || displayName || "Room Report",
    subtitle: displayName ? `Unit: ${displayName}` : undefined,
  };

  const eng = new SectionEngine(pdf, headerCtx, { margin: 10 });
  eng.startFirstPage();

  if (sourceEl && document.body.contains(sourceEl)) {
    // Live DOM capture — delegate to the shared pipeline.
    await captureLiveSourceAndAddPages(pdf, eng, sourceEl);
  } else {
    // Programmatic path — build sections individually so EngineSelectionSummary
    // is always present regardless of whether sourceEl exists.
    const optimalPoint = result?.raw?.results?.[0]?.optimal_point ?? result?.raw?.optimal_point ?? null;

    const htmlSections = [
      projectInfoBlock(project, [room]),
      designConditionsHTML(project, isSI, room.unit_model || null),
      optimalPoint ? engineSelectionSummaryHTML(optimalPoint, isSI) : "",
      roomReportBody(room, result),
    ].filter(Boolean);

    for (const html of htmlSections) {
      if (!html) continue;
      await eng.addHTMLSection(html, { antiOrphanSelector: "[data-subtitle-anchor]" });
    }
  }

  eng.stampPageNumbers();

  const safe = (room.name || "Room").replace(/[^a-zA-Z0-9-]/g, "_");
  pdf.save(`ZAMBA_Room_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`);
  } finally {
    if (wasDark) htmlEl.classList.add("dark");
  }
}

export async function generateProjectSummaryPDF({ project, rooms, results, configurations, isSI = false, returnBlob = false, summaryOnly = false }) {
  _isSI = isSI;
  const _htmlEl2 = document.documentElement;
  const _wasDark2 = _htmlEl2.classList.contains("dark");
  if (_wasDark2) _htmlEl2.classList.remove("dark");
  await new Promise((r) => requestAnimationFrame(r));
  await new Promise((r) => requestAnimationFrame(r));
  try {
  const logo = await preloadLogo();
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  // Page 1: Polished cover page (no universal header — cover has its own branding)
  await renderCoverPage(pdf, logo, project, rooms, results, "Unit Schedule");

  const hasModelN = (rooms || []).some((r) => r.unit_model === "Model N" || r.unit_model === "Model N-f");
  const baseHeaderCtx = {
    logoDataUrl: logo,
    projectName: project?.name || "Project",
    projectNumber: project?.number || undefined,
  };

  // Page 2+: Schedule of Units
  // Add page manually so we can track only content pages (not the cover) in engine.
  pdf.addPage();
  const eng = new SectionEngine(pdf, { ...baseHeaderCtx, title: "Unit Schedule" }, { margin: 10 });
  // startFirstPage draws the header on the page that already exists (page 2)
  eng.startFirstPage();

  // Project info + design conditions — rendered once, before the room schedule.
  await eng.addHTMLSection(
    projectInfoBlock(project, rooms) +
    designConditionsHTML(project, isSI, hasModelN ? "Model N" : "Model O-2"),
    { antiOrphanSelector: "[data-subtitle-anchor]" }
  );

  // Room Schedule — chunked so each html2canvas pass covers at most
  // SCHEDULE_CHUNK_SIZE rows. Memory peak is O(chunk) not O(232 rooms).
  // Landscape orientation gives 297mm of horizontal width (vs 210mm portrait)
  // which is what makes 15+ columns + Room Name + Unit Model fit on a single
  // line per row — was wrapping to 2 lines in portrait, doubling page count.
  // Yield the main thread between chunks so V8 GC can reclaim the previous
  // canvas + container before the next pass begins.
  const schedChunks = scheduleTableChunks(rooms, results, SCHEDULE_CHUNK_SIZE, project);
  for (const chunkHtml of schedChunks) {
    await eng.addHTMLSection(chunkHtml, {
      antiOrphanSelector: "[data-subtitle-anchor]",
      orientation: "landscape",
    });
    // Yield to allow browser GC between large table renders.
    await new Promise((r) => setTimeout(r, 0));
  }

  // Unit Schedule (per-variant summary) — rendered as a separate section
  // after all room-schedule chunks complete.
  const unitSchedHtml = unitScheduleTable(rooms, results);
  if (unitSchedHtml) {
    await eng.addHTMLSection(unitSchedHtml, { antiOrphanSelector: "[data-subtitle-anchor]" });
  }

  // The heavy sections — Variant Summary, Configuration Schedule (BOM), and
  // sizing footnotes — are deliberately skipped in summaryOnly mode. On a
  // 232-room project the Configuration Schedule alone is a 232-row HTML table
  // that html2canvas cannot render in a single pass without OOMing the tab.
  // The Schedule Summary PDF intentionally contains just project info,
  // design conditions, the chunked room schedule, and the unit schedule.
  // The Detailed Report flow handles per-room detail in batches; BOM /
  // variant summary live in the Submittal Package (which has its own
  // dedicated heavy-table pipeline).
  if (!summaryOnly) {
    const cfgHtml = configurationScheduleHTML({ rooms, results, configurations, isSI });
    if (cfgHtml) {
      const summaryHtml = variantSummaryHTML({ rooms, results, configurations, isSI });
      if (summaryHtml) {
        eng._headerCtx = { ...baseHeaderCtx, title: "Variant Summary" };
        eng._addPage();
        await eng.addHTMLSection(summaryHtml, { antiOrphanSelector: "[data-subtitle-anchor]" });
      }
      eng._headerCtx = { ...baseHeaderCtx, title: "Configuration Schedule" };
      eng._addPage();
      await eng.addHTMLSection(cfgHtml, { antiOrphanSelector: "[data-subtitle-anchor]" });
    }

    // Sizing footnote pages — one per product family
    const footnoteFamilies = buildSizingFootnotePages(logo, project, rooms);
    for (const fam of footnoteFamilies) {
      eng._headerCtx = { ...baseHeaderCtx, title: `Sizing — ${fam.familyLabel.split("—")[0].trim()}` };
      eng._addPage();
      await eng.addHTMLSection(fam.bodyHtml);
    }
  }

  eng.stampPageNumbers();

  const safe = (project.name || "Project").replace(/[^a-zA-Z0-9-]/g, "_");
  if (returnBlob) {
    return pdf.output("blob");
  }
  pdf.save(`ZAMBA_${safe}_Schedule_${new Date().toISOString().slice(0, 10)}.pdf`);
  } finally {
    if (_wasDark2) _htmlEl2.classList.add("dark");
  }
}

// ---------------------------------------------------------------------------
// generateDetailedRoomReportsPDF — batched per-room detail PDF.
//
// Processes selectedRoomIdxs in batches of DETAIL_BATCH_SIZE. Each batch
// creates its own jsPDF instance, renders its rooms via the programmatic
// roomReportBody + SectionEngine pipeline (same as the non-sourceEl path in
// generateRoomReportPDF), then exports to a Blob and immediately discards the
// jsPDF object so V8 can GC it. After all batches, pdf-lib merges the blobs
// into a single output file.
//
// onProgress({ done, total, label }) is called after each batch.
// Returns a Blob when returnBlob=true; otherwise triggers a browser download.
// ---------------------------------------------------------------------------

const DETAIL_BATCH_SIZE = 20;

export async function generateDetailedRoomReportsPDF({
  project,
  rooms,
  results,
  selectedRoomIdxs,
  isSI = false,
  returnBlob = false,
  onProgress = null,
}) {
  if (!selectedRoomIdxs || selectedRoomIdxs.length === 0) {
    throw new Error("No rooms selected for the Detailed Report.");
  }

  _isSI = isSI;
  const htmlEl = document.documentElement;
  const wasDark = htmlEl.classList.contains("dark");
  if (wasDark) htmlEl.classList.remove("dark");
  // Two frames so the browser settles into light mode before any captures.
  await new Promise((r) => requestAnimationFrame(r));
  await new Promise((r) => requestAnimationFrame(r));

  try {
    const logo = await preloadLogo();
    const baseHeaderCtx = {
      logoDataUrl: logo,
      projectName: project?.name || "Project",
      projectNumber: project?.number || undefined,
    };

    const batchBlobs = [];
    const totalRooms = selectedRoomIdxs.length;

    for (let batchStart = 0; batchStart < selectedRoomIdxs.length; batchStart += DETAIL_BATCH_SIZE) {
      const batchIdxs = selectedRoomIdxs.slice(batchStart, batchStart + DETAIL_BATCH_SIZE);

      // Each batch gets its own jsPDF — allows GC after blobification.
      const batchPdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      let isFirstPageInBatch = true;

      for (const roomIdx of batchIdxs) {
        const room = rooms[roomIdx];
        const result = results[roomIdx];
        if (!room) continue;

        const roomHeaderCtx = {
          ...baseHeaderCtx,
          title: `Room Detail — ${room.name || `Room ${roomIdx + 1}`}`,
          subtitle: room.unit_model || undefined,
        };

        if (!isFirstPageInBatch) {
          batchPdf.addPage();
        }
        isFirstPageInBatch = false;

        const roomEng = new SectionEngine(batchPdf, roomHeaderCtx, { margin: 10 });
        roomEng.startFirstPage();

        // Live-DOM capture path — identical to generateSubmittalPDF's per-room
        // rendering. Mounts the real RoomReportPage off-screen so all sections
        // (ERV stages, green delivered banner, heating, psychrometric chart) are
        // captured at full fidelity.
        await captureRoomReportToPdf(batchPdf, roomEng, {
          project,
          room,
          result,
          isSI,
          lengthFt: null,
        });

        roomEng.stampPageNumbers();
      }

      // Export batch to Blob, then release the jsPDF object.
      batchBlobs.push(batchPdf.output("blob"));

      const doneCount = Math.min(batchStart + DETAIL_BATCH_SIZE, totalRooms);
      if (onProgress) {
        const batchRoomNames = batchIdxs
          .map((idx) => rooms[idx]?.name || `Room ${idx + 1}`)
          .join(", ");
        onProgress({
          done: doneCount,
          total: totalRooms,
          label: `Batch ${Math.floor(batchStart / DETAIL_BATCH_SIZE) + 1} / ${Math.ceil(totalRooms / DETAIL_BATCH_SIZE)} · ${batchRoomNames}`,
        });
      }

      // Yield the main thread so V8 can GC the completed batch jsPDF before
      // the next iteration allocates a new one.
      await new Promise((r) => setTimeout(r, 0));
    }

    // Merge all batch blobs into a single PDF via pdf-lib.
    const { PDFDocument } = await import("pdf-lib");
    const merged = await PDFDocument.create();

    for (const blob of batchBlobs) {
      const arrayBuf = await blob.arrayBuffer();
      const src = await PDFDocument.load(arrayBuf, { ignoreEncryption: true });
      const copiedPages = await merged.copyPages(src, src.getPageIndices());
      copiedPages.forEach((p) => merged.addPage(p));
      // Let the src PDFDocument go out of scope; the arrayBuf byte data is
      // now embedded in `merged` — we don't need the original blob bytes.
    }

    const mergedBytes = await merged.save();
    const outputBlob = new Blob([mergedBytes], { type: "application/pdf" });

    if (returnBlob) return outputBlob;

    const safe = (project.name || "Project").replace(/[^a-zA-Z0-9-]/g, "_");
    const stamp = new Date().toISOString().slice(0, 10);
    const filename = `ZAMBA_${safe}_DetailedRooms_${stamp}.pdf`;
    const url = URL.createObjectURL(outputBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5_000);
  } finally {
    if (wasDark) htmlEl.classList.add("dark");
  }
}

// --- Cover Page ---
// Cover renderer reused by Selection / Config / Submittal Package flows. The
// Submittal tab calls `generateSubmittalCoverPdf` below to obtain a single-page
// blob with the same look that it can then merge into its compiled package.
async function renderCoverPage(pdf, logo, project, rooms, results, coverTitle = "Selection Submittal") {
  const okCount = results.filter((r) => r?.ok).length;
  // Mirror projectInfoBlock's rule: only show the design-conditions country
  // on the cover when the project includes a Model N / N-f. Otherwise we'd
  // leak a stored default (e.g. "USA / Anchorage") into induction-only
  // submittals.
  const isModelNLike = (m) => m === "Model N" || m === "Model N-f";
  const coverNeedsOutdoor = (rooms || []).some((r) => isModelNLike(r.unit_model));
  const dc = project.designConditions || {};
  const coverCountry = coverNeedsOutdoor
    ? (dc.country === "__other__" ? (dc.country_other || "") : (dc.country || ""))
    : "";
  const html = `
    <div style="height:1100px;display:flex;flex-direction:column;justify-content:space-between;padding:40px;font-family:system-ui,sans-serif;color:#0f172a;">
      <div>
        <div style="text-align:center;padding:60px 0 40px;border-bottom:3px solid #0f172a;">
          ${logo ? `<img src="${logo}" style="height:80px;margin-bottom:20px;" />` : '<div style="font-size:42px;font-weight:800;letter-spacing:2px;">ZAMBA</div>'}
          <div style="font-size:14px;color:#64748b;letter-spacing:4px;text-transform:uppercase;">${coverTitle}</div>
        </div>
        <div style="margin-top:80px;text-align:center;">
          <div style="font-size:14px;color:#64748b;letter-spacing:2px;text-transform:uppercase;margin-bottom:10px;">Project</div>
          <div style="font-size:36px;font-weight:700;line-height:1.2;margin-bottom:8px;">${project.name || "—"}</div>
          ${project.number ? `<div style="font-size:18px;color:#475569;">Project No.: ${project.number}</div>` : ""}
          ${project.address || project.cityText || project.state || coverCountry ? `<div style="font-size:14px;color:#475569;margin-top:24px;">
            ${project.address ? `${project.address}<br/>` : ""}
            ${[project.cityText, project.state, coverCountry].filter(Boolean).join(", ")}
          </div>` : ""}
        </div>
        <div style="margin-top:80px;display:flex;justify-content:space-around;text-align:center;">
          <div>
            <div style="font-size:36px;font-weight:700;color:#0f172a;">${rooms.length}</div>
            <div style="font-size:11px;color:#64748b;letter-spacing:2px;text-transform:uppercase;margin-top:4px;">Rooms</div>
          </div>
          <div>
            <div style="font-size:36px;font-weight:700;color:#10b981;">${okCount}</div>
            <div style="font-size:11px;color:#64748b;letter-spacing:2px;text-transform:uppercase;margin-top:4px;">Selections</div>
          </div>
          <div>
            <div style="font-size:36px;font-weight:700;color:#0f172a;">${[...new Set(rooms.map((r) => r.unit_model).filter(Boolean))].length}</div>
            <div style="font-size:11px;color:#64748b;letter-spacing:2px;text-transform:uppercase;margin-top:4px;">Models</div>
          </div>
        </div>
      </div>
      <div style="text-align:center;border-top:2px solid #e2e8f0;padding-top:20px;">
        <div style="font-size:12px;color:#64748b;">Generated ${fmtDate()}</div>
        <div style="font-size:11px;color:#94a3b8;margin-top:6px;">ZAMBA Product Selector — www.zambahvac.com</div>
      </div>
    </div>
  `;
  const container = document.createElement("div");
  container.style.cssText =
    "position:absolute;left:-9999px;top:0;width:794px;background:#ffffff;color-scheme:light;";
  container.innerHTML = html;
  // withLightTheme wraps appendChild through canvas return so dark: variants
  // don't render on the off-screen cover page container.
  const canvas = await withLightTheme(async () => {
    document.body.appendChild(container);
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
    return html2canvas(container, {
      scale: 1.5,
      useCORS: true,
      backgroundColor: "#ffffff",
      onclone: (doc) => {
        doc.documentElement.classList.remove("dark");
        doc.body.classList.remove("dark");
      },
    });
  });
  try {
    const pageWmm = pdf.internal.pageSize.getWidth();
    const margin = 0;
    const targetW = pageWmm - margin * 2;
    const ratio = targetW / canvas.width;
    pdf.addImage(
      canvas.toDataURL("image/jpeg", 0.85),
      "JPEG",
      margin,
      margin,
      targetW,
      canvas.height * ratio,
      undefined,
      "FAST"
    );
  } finally {
    document.body.removeChild(container);
  }
}

// --- Bundled Submittal PDF: Cover + TOC + Detailed Selection Report +
//     Variant Summary + Configuration Schedule + Drawings ---
// The previous full-page per-room reports have been folded into the
// "Detailed Selection Report" — one consolidated table with the Variant column.
// `RoomReportPageComponent` is no longer used but accepted to keep the call
// signature stable for the Summary tab caller.
// roomPages is accepted for caller backward-compat but no longer used —
// per-room rendering now goes through captureRoomReportToPdf directly.
// eslint-disable-next-line no-unused-vars
export async function generateSubmittalPDF({ project, rooms, results, configurations, isSI = false, RoomReportPageComponent, roomPages = [], includeConfigSchedule = false, returnBlob = false, configReportOnly = false }) {
  _isSI = isSI;
  const _htmlEl3 = document.documentElement;
  const _wasDark3 = _htmlEl3.classList.contains("dark");
  if (_wasDark3) _htmlEl3.classList.remove("dark");
  await new Promise((r) => requestAnimationFrame(r));
  await new Promise((r) => requestAnimationFrame(r));
  try {
  const logo = await preloadLogo();
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  // Section start pages — captured as we build. Every section AFTER the cover
  // gets +1 to its captured page number because the TOC will be inserted at
  // position 2 (via pdf-lib at the end), shifting all subsequent pages down by one.
  const sections = [];
  // noteSection records the NEXT page number (after TOC offset).
  const noteSection = (label) => {
    const pageNum = pdf.internal.getNumberOfPages() + 1; // +1 for TOC insertion
    sections.push({ label, page: pageNum });
  };

  // ===== Page 1: Cover =====
  let coverTitle = "Selection Report";
  if (configReportOnly) coverTitle = "Configuration Schedule";
  else if (includeConfigSchedule) coverTitle = "Submittal Package";
  await renderCoverPage(pdf, logo, project, rooms, results, coverTitle);
  sections.push({ label: "Cover Page", page: 1 });

  const hasModelN = (rooms || []).some((r) => r.unit_model === "Model N" || r.unit_model === "Model N-f");
  const baseHeaderCtx = {
    logoDataUrl: logo,
    projectName: project?.name || "Project",
    projectNumber: project?.number || undefined,
  };

  // Helper: initialise a SectionEngine on a freshly added page.
  const makeEng = (title) => {
    pdf.addPage();
    const eng = new SectionEngine(pdf, { ...baseHeaderCtx, title }, { margin: 10 });
    eng.startFirstPage();
    return eng;
  };

  // lastRoomEng is hoisted to outer scope so the footnote-packing logic below
  // can attempt to place the first sizing footnote onto the last room's
  // remaining content area rather than forcing a new page.
  let lastRoomEng = null;

  if (!configReportOnly) {
    // ===== Section: Selection Summary by Room =====
    noteSection("Selection Summary by Room");
    const selEng = makeEng("Selection Summary by Room");
    await selEng.addHTMLSection(
      projectInfoBlock(project, rooms) +
      designConditionsHTML(project, isSI, hasModelN ? "Model N" : "Model O-2") +
      detailedSelectionReportTable(rooms, results, configurations, project),
      { antiOrphanSelector: "[data-subtitle-anchor]" }
    );
    selEng.stampPageNumbers();

    // ===== Section: Variant Summary (by length) =====
    const variantHtml = unitScheduleTable(rooms, results);
    if (variantHtml) {
      noteSection("Variant Summary (by Length)");
      const varEng = makeEng("Variant Summary (by Length)");
      await varEng.addHTMLSection(variantHtml, { antiOrphanSelector: "[data-subtitle-anchor]" });
      varEng.stampPageNumbers();
    }

    // ===== Section: Room-by-Room Detailed Selection =====
    // Each room is rendered via captureRoomReportToPdf — identical to the
    // standalone Room Report PDF. The SectionEngine handles headers and
    // page registration so page numbers align with the TOC.
    const hasOkRooms = rooms.some((r, i) => results[i]?.ok);
    if (hasOkRooms) {
      noteSection("Room-by-Room Detailed Selection");
      for (let ri = 0; ri < rooms.length; ri++) {
        const room = rooms[ri];
        const result = results[ri];
        if (!result?.ok) continue;
        // Build the full per-room header BEFORE creating the engine so the
        // first page's drawHeader call uses the room name + subtitle.
        const roomHeaderCtx = {
          ...baseHeaderCtx,
          title: `Room Detail — ${room.name || `Room ${ri + 1}`}`,
          subtitle: room.unit_model || undefined,
        };
        pdf.addPage();
        const roomEng = new SectionEngine(pdf, roomHeaderCtx, { margin: 10 });
        roomEng.startFirstPage();
        try {
          await captureRoomReportToPdf(pdf, roomEng, {
            project,
            room,
            result,
            isSI,
            lengthFt: null,
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`Room detail capture failed for ${room.name}:`, err);
        }
        roomEng.stampPageNumbers();
        lastRoomEng = roomEng;
      }
    }
  }

  // ===== Section: Configuration Schedule =====
  const cfgHtml = (includeConfigSchedule || configReportOnly)
    ? configurationScheduleHTML({ rooms, results, configurations, isSI })
    : "";
  if (cfgHtml) {
    sections.push({
      label: "Configuration Schedule (Inclusions & Exclusions)",
      page: pdf.internal.getNumberOfPages() + 1, // +1 for TOC insertion
    });
    // For configReportOnly, design conditions haven't appeared yet — prepend them.
    const cfgDesignConditions = configReportOnly
      ? projectInfoBlock(project, rooms) + designConditionsHTML(project, isSI, hasModelN ? "Model N" : "Model O-2")
      : "";
    const cfgEng = makeEng("Configuration Schedule");
    await cfgEng.addHTMLSection(cfgDesignConditions + cfgHtml, { antiOrphanSelector: "[data-subtitle-anchor]" });
    cfgEng.stampPageNumbers();
    // Config schedule section consumes the last-room engine slot — footnote
    // must get its own page in this branch.
    lastRoomEng = null;
  }

  // ===== Sizing footnote pages =====
  // Footnote-packing: if there is exactly one footnote family and it is small
  // enough to fit in the trailing whitespace of the last room's final page,
  // append it there instead of opening a new page (saves 1 page in the common
  // single-room EWS project).
  //   ceiling: 75 mm  — raised from 60 to account for ambient line-height / padding
  //                      increases that made the footnote fractionally taller.
  //   slack:    1 mm  — tightened from +4 so the fit predicate passes when the
  //                      cursor sits just 1 mm above the minimum safe margin.
  if (!configReportOnly) {
    const footnoteFamilies = buildSizingFootnotePages(logo, project, rooms);
    let packedFirst = false;
    if (lastRoomEng && footnoteFamilies.length >= 1) {
      const fam = footnoteFamilies[0];
      // Measure the footnote body at the same 794 px width used by _measureHTMLCanvas.
      const { canvas: fnCanvas, container: fnContainer } = await _measureHTMLCanvas(fam.bodyHtml);
      document.body.removeChild(fnContainer);
      const ratio = 794 / 210; // px-per-mm at the _measureHTMLCanvas render width
      const fnHeightMm = fnCanvas.height / ratio;
      if (fnHeightMm <= 75 && lastRoomEng._contentAvailH() >= fnHeightMm + 1) {
        // Fits — append directly onto the last room's page without a new page.
        noteSection(`Sizing Method — ${fam.familyLabel}`);
        await lastRoomEng.addHTMLSection(fam.bodyHtml);
        lastRoomEng.stampPageNumbers();
        packedFirst = true;
      }
    }
    for (let fi = packedFirst ? 1 : 0; fi < footnoteFamilies.length; fi++) {
      const fam = footnoteFamilies[fi];
      noteSection(`Sizing Method — ${fam.familyLabel}`);
      const fnEng = makeEng(`Sizing — ${fam.familyLabel.split("—")[0].trim()}`);
      await fnEng.addHTMLSection(fam.bodyHtml);
      fnEng.stampPageNumbers();
    }
  }

  // ===== Drawings =====
  // PDF-2 Rule 5: drawings live ONLY in the Submittal Package (assembled by
  // SubmittalPackageTab.jsx via its own pdf-lib pipeline). Previously this
  // function also appended `drawingsForRooms(...)` here, which meant a user
  // generating both the Selection Report and the Submittal Package ended up
  // with two copies of the same drawings. Removed entirely from this
  // generator — SubmittalPackageTab is the single source of drawings now.

  const safe = (project.name || "Project").replace(/[^a-zA-Z0-9-]/g, "_");
  let docLabel = "SelectionReport";
  if (configReportOnly) docLabel = "ConfigReport";
  else if (includeConfigSchedule) docLabel = "SubmittalPackage";
  const filename = `ZAMBA_${safe}_${docLabel}_${new Date().toISOString().slice(0, 10)}.pdf`;

  // ---- pdf-lib: insert TOC page at index 1 + append drawings ----
  try {
    const pdfLib = await import("pdf-lib");
    const { PDFDocument, StandardFonts, rgb } = pdfLib;
    const baseBytes = pdf.output("arraybuffer");
    const merged = await PDFDocument.load(baseBytes);

    // Draw the TOC NATIVELY in pdf-lib at index 1 (so it lands as page 2).
    if (sections.length >= 2) {
      const tocPage = merged.insertPage(1, [595.28, 841.89]); // A4 portrait
      const helv = await merged.embedFont(StandardFonts.Helvetica);
      const helvBold = await merged.embedFont(StandardFonts.HelveticaBold);
      const w = tocPage.getWidth();
      const h = tocPage.getHeight();
      const margin = 50;
      const darkColor = rgb(0.058, 0.090, 0.165); // #0f172a
      const grayColor = rgb(0.46, 0.50, 0.55);    // #75808c
      const lightColor = rgb(0.59, 0.66, 0.72);   // ~slate-400

      // Top border line
      tocPage.drawLine({
        start: { x: margin, y: h - margin - 12 },
        end:   { x: w - margin, y: h - margin - 12 },
        thickness: 0.8,
        color: darkColor,
      });

      // Project name (right) + brand (left)
      tocPage.drawText("ZAMBA", {
        x: margin, y: h - margin - 4,
        size: 14, font: helvBold, color: darkColor,
      });
      const projectName = project.name || "Project";
      const projWidth = helv.widthOfTextAtSize(projectName, 10);
      tocPage.drawText(projectName, {
        x: w - margin - projWidth, y: h - margin - 4,
        size: 10, font: helv, color: grayColor,
      });

      // Title
      tocPage.drawText("Table of Contents", {
        x: margin, y: h - margin - 48,
        size: 24, font: helvBold, color: darkColor,
      });

      // Entries
      const rowH = 24;
      let y = h - margin - 90;
      const labelX = margin + 32;
      const pageNumSize = 12;
      const labelSize = 12;
      sections.forEach((s, idx) => {
        const num = String(idx + 1).padStart(2, "0");
        // Number
        tocPage.drawText(num, {
          x: margin, y, size: pageNumSize, font: helvBold, color: darkColor,
        });
        // Label
        tocPage.drawText(s.label, {
          x: labelX, y, size: labelSize, font: helv, color: darkColor,
        });
        // Page number (right-aligned)
        const pageStr = String(s.page);
        const pageW = helvBold.widthOfTextAtSize(pageStr, pageNumSize);
        tocPage.drawText(pageStr, {
          x: w - margin - pageW, y, size: pageNumSize, font: helvBold, color: darkColor,
        });
        // Leader dots
        const labelWidth = helv.widthOfTextAtSize(s.label, labelSize);
        const dotsStart = labelX + labelWidth + 8;
        const dotsEnd = w - margin - pageW - 8;
        if (dotsEnd > dotsStart) {
          const dotW = helv.widthOfTextAtSize(".", labelSize);
          const dotCount = Math.floor((dotsEnd - dotsStart) / (dotW * 2.2));
          if (dotCount > 0) {
            tocPage.drawText(" .".repeat(dotCount).trim(), {
              x: dotsStart, y, size: labelSize, font: helv, color: lightColor,
            });
          }
        }
        y -= rowH;
      });

      // Footer
      const fStr = `Generated ${fmtDate()}`;
      const fW = helv.widthOfTextAtSize(fStr, 8);
      tocPage.drawText(fStr, {
        x: (w - fW) / 2, y: 30, size: 8, font: helv, color: grayColor,
      });
    }

    // PDF-2 Rule 5: drawings used to be appended here. Removed — see the
    // comment above the safe-name block for context.

    const out = await merged.save();
    const blob = new Blob([out], { type: "application/pdf" });
    if (returnBlob) {
      return blob;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("pdf-lib TOC/merge failed, saving base PDF only:", err);
    if (returnBlob) {
      return pdf.output("blob");
    }
    pdf.save(filename);
  }
  } finally {
    if (_wasDark3) _htmlEl3.classList.add("dark");
  }
}

// ---------------------------------------------------------------------------
// Standalone cover-only generator used by the Submittal Package compiler.
// Returns a single-page A4 PDF Blob using the exact same cover layout as the
// Selection Report / Config Report so the final package looks visually
// consistent across all three flavours.
// ---------------------------------------------------------------------------
export async function generateSubmittalCoverPdf({
  project, rooms = [], results = [], coverTitle = "Submittal Package", isSI = false,
}) {
  _isSI = !!isSI;
  const _htmlEl4 = document.documentElement;
  const _wasDark4 = _htmlEl4.classList.contains("dark");
  if (_wasDark4) _htmlEl4.classList.remove("dark");
  await new Promise((r) => requestAnimationFrame(r));
  await new Promise((r) => requestAnimationFrame(r));
  try {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const logo = await preloadLogo();
    await renderCoverPage(pdf, logo, project, rooms, results, coverTitle);
    return pdf.output("blob");
  } finally {
    if (_wasDark4) _htmlEl4.classList.add("dark");
  }
}

// ---------------------------------------------------------------------------
// captureRoomReportToPdf — mount RoomReportPage off-screen, then capture it
// into `pdf` via captureLiveSourceAndAddPages (the same fully-fixed pipeline
// used by the standalone Room Report path in generateRoomReportPDF).
//
// Parameters:
//   pdf       — the jsPDF instance
//   eng       — a SectionEngine already initialised on the correct page.
//               Pages are added via eng._addPage() so they are registered in
//               eng._numberedPages and receive the correct header + footer.
//   opts      — { project, room, result, isSI, lengthFt }
//
// The container is mounted at 1080px wide with NO inline padding so that
// RoomReportPage's own `border border-border rounded p-4 bg-card` reportRef
// div provides the padding — matching the live wizard layout exactly.
//
// Does NOT call stampPageNumbers — the caller's responsibility.
// ---------------------------------------------------------------------------
async function captureRoomReportToPdf(pdf, eng, { project, room, result, isSI: siFlag, lengthFt }) {
  // Lazy-import RoomReportPage to avoid circular deps at module load time.
  const { default: RoomReportPage } = await import("../components/wizard/RoomReportPage");

  const container = document.createElement("div");
  // 1080px matches the typical max-w-[1080px] wizard layout.
  // No inline padding — RoomReportPage's reportRef div (p-4) provides it.
  container.style.cssText =
    "position:absolute;left:-9999px;top:0;width:1080px;background:#ffffff;color-scheme:light;";

  let root;
  try {
    // Mount React component inside withLightTheme so Tailwind dark: variants
    // never fire on the off-screen container. html2canvas also runs inside so
    // its onclone gets a light-mode document.
    await withLightTheme(async () => {
      document.body.appendChild(container);
      root = createRoot(container);

      await new Promise((resolve) => {
        root.render(
          React.createElement(RoomReportPage, {
            project,
            room,
            result,
            isSI: siFlag,
            captureMode: true,
            showChart: true,
            lockedLength: lengthFt,
            onBack: () => {},
          })
        );
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

      // Additional frames so computed styles settle in light mode.
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));

      // Target the reportRef div so we capture exactly what the standalone
      // Room Report PDF captures (border, padding, bg-card included).
      const reportEl = container.querySelector('[data-testid="room-report-page"]') || container;

      // Delegate to the shared fully-fixed capture pipeline.
      await captureLiveSourceAndAddPages(pdf, eng, reportEl);
    });
  } finally {
    if (root) root.unmount();
    if (container.parentNode) document.body.removeChild(container);
  }
}

// ---------------------------------------------------------------------------
// Single Unit Multi-Length Report (Mode A / Mode B sweep)
//
// Page 1: cover + summary table (programmatic jsPDF — no html2canvas).
// Pages 2…N: one detail page per selected length, captured from RoomReportPage
//   via html2canvas inside withLightTheme. Each length starts on a new page.
//
// rows shape (each): { length, cooling_total, cooling_sensible, cooling_latent,
//   heating, airflow, water_flow, water_pd }
// entries shape (each): { length, result, room } — raw sweep entry objects.
//   Optional: when omitted (legacy callers), only the summary table is produced.
// ---------------------------------------------------------------------------
export async function generateSingleUnitMultiLengthReport({ model, project, rows, entries = [], isSI = false }) {
  _isSI = !!isSI;
  const _htmlEl5 = document.documentElement;
  const _wasDark5 = _htmlEl5.classList.contains("dark");
  if (_wasDark5) _htmlEl5.classList.remove("dark");
  await new Promise((r) => requestAnimationFrame(r));
  await new Promise((r) => requestAnimationFrame(r));
  try {
  const logo = await preloadLogo();
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });

  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const contentW = pageW - margin * 2;

  const headerCtx = {
    logoDataUrl: logo,
    projectName: project?.name || model || "Single Unit",
    title: "Single Unit — Max Performance",
    subtitle: model,
  };

  // ---- Page 1: Universal header + design conditions + summary table ----
  let cursorY = drawPageHeaderBand(pdf, headerCtx);

  // Project info line
  if (project?.name) {
    pdf.setFontSize(8);
    pdf.setFont(undefined, "normal");
    pdf.setTextColor(71, 85, 105);
    const infoStr = [
      project.name,
      project.number ? `No. ${project.number}` : "",
      fmtDate(),
    ].filter(Boolean).join("  ·  ");
    pdf.text(infoStr, margin, cursorY);
    cursorY += 6;
  }

  // ---- Design Conditions section ----
  cursorY = drawDesignConditionsToPdf(pdf, project, isSI, cursorY, { margin, contentW, model });

  // ---- Summary Table ----
  const COL_DEFS = [
    { header: isSI ? "Length (m)" : "Length (ft)", key: "length",
      fmt: (v) => v != null ? (isSI ? `${(v * 0.3048).toFixed(2)}` : `${v}`) : "—",
      w: 22, align: "center" },
    { header: "Total Cool.\n(BTU/h)", key: "cooling_total",
      fmt: (v) => fmtNum(v), w: 28, align: "right" },
    { header: "Sensible\n(BTU/h)", key: "cooling_sensible",
      fmt: (v) => fmtNum(v), w: 26, align: "right" },
    { header: "Latent\n(BTU/h)", key: "cooling_latent",
      fmt: (v) => fmtNum(v), w: 24, align: "right" },
    { header: "Heating\n(BTU/h)", key: "heating",
      fmt: (v) => fmtNum(v), w: 26, align: "right" },
    { header: "Airflow\n(CFM)", key: "airflow",
      fmt: (v) => fmtNum(v), w: 22, align: "right" },
    { header: "Water Flow\n(GPM)", key: "water_flow",
      fmt: (v) => fmtNum(v, 2), w: 26, align: "right" },
    { header: "Press. Drop\n(ft H₂O)", key: "water_pd",
      fmt: (v) => fmtNum(v, 3), w: 26, align: "right" },
  ];

  const rowH = 9;
  const headerH = 12;
  const cellPad = 2.5;

  const totalColW = COL_DEFS.reduce((s, c) => s + c.w, 0);
  const tableScale = contentW / totalColW;
  const cols = COL_DEFS.map((c) => ({ ...c, w: c.w * tableScale }));

  // Table header row
  pdf.setFillColor(15, 23, 42);
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(7);
  pdf.setFont(undefined, "bold");
  pdf.rect(margin, cursorY, contentW, headerH, "F");

  let cx = margin;
  cols.forEach((col) => {
    const lines = col.header.split("\n");
    const lineH = 4;
    const textY = cursorY + headerH / 2 - (lines.length * lineH) / 2 + 3;
    lines.forEach((line, li) => {
      if (col.align === "right") pdf.text(line, cx + col.w - cellPad, textY + li * lineH, { align: "right" });
      else if (col.align === "center") pdf.text(line, cx + col.w / 2, textY + li * lineH, { align: "center" });
      else pdf.text(line, cx + cellPad, textY + li * lineH);
    });
    cx += col.w;
  });
  cursorY += headerH;

  // Table body rows
  rows.forEach((row, ri) => {
    if (ri % 2 === 0) {
      pdf.setFillColor(241, 245, 249);
      pdf.rect(margin, cursorY, contentW, rowH, "F");
    }
    pdf.setTextColor(15, 23, 42);
    pdf.setFont(undefined, ri === 0 ? "bold" : "normal");
    pdf.setFontSize(8);
    let cx2 = margin;
    cols.forEach((col) => {
      const val = col.fmt(row[col.key]);
      const textY2 = cursorY + rowH / 2 + 2.5;
      if (col.align === "right") pdf.text(val, cx2 + col.w - cellPad, textY2, { align: "right" });
      else if (col.align === "center") pdf.text(val, cx2 + col.w / 2, textY2, { align: "center" });
      else pdf.text(val, cx2 + cellPad, textY2);
      cx2 += col.w;
    });
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.2);
    pdf.line(margin, cursorY + rowH, margin + contentW, cursorY + rowH);
    cursorY += rowH;
  });

  // Table outer border + column dividers
  const tableH = headerH + rows.length * rowH;
  const tableTopY = cursorY - rows.length * rowH - headerH;
  pdf.setDrawColor(15, 23, 42);
  pdf.setLineWidth(0.5);
  pdf.rect(margin, tableTopY, contentW, tableH);
  pdf.setLineWidth(0.2);
  pdf.setDrawColor(203, 213, 225);
  let divX = margin;
  cols.slice(0, -1).forEach((col) => {
    divX += col.w;
    pdf.line(divX, tableTopY, divX, tableTopY + tableH);
  });

  // Footer for page 1 (placeholder — stamped with N/M after all pages built)
  drawPageFooter(pdf, 1, null);

  // ---- Per-length detail pages ----
  // Each detail page uses a per-entry SectionEngine so captureRoomReportToPdf
  // can manage page headers via eng._addPage(). After capture, the engine's
  // numbered pages are used for the two-pass footer stamp below.
  const sortedEntries = [...entries].sort((a, b) => (a.length || 0) - (b.length || 0));
  const detailEngines = [];
  for (const entry of sortedEntries) {
    if (!entry?.result?.ok || !entry.room) continue;
    try {
      const entryHeaderCtx = {
        ...headerCtx,
        subtitle: entry.length ? `${isSI ? `${(entry.length * 0.3048).toFixed(2)} m` : `${entry.length} ft`}` : model,
      };
      pdf.addPage();
      const entryEng = new SectionEngine(pdf, entryHeaderCtx, { margin: 10 });
      entryEng.startFirstPage();
      await captureRoomReportToPdf(pdf, entryEng, {
        project,
        room: { ...entry.room, unit_model: model },
        result: entry.result,
        isSI,
        lengthFt: entry.length,
      });
      detailEngines.push(entryEng);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`Detail page failed for ${entry.length} ft:`, err);
    }
  }

  // ---- Two-pass: stamp page 1 footer with total page count ----
  const totalPages = pdf.internal.getNumberOfPages();
  pdf.setPage(1);
  drawPageFooter(pdf, 1, totalPages);
  // Stamp footers on all detail pages tracked by the per-entry engines.
  for (const entryEng of detailEngines) {
    for (const pageNum of entryEng._numberedPages) {
      pdf.setPage(pageNum);
      drawPageFooter(pdf, pageNum, totalPages);
    }
  }

  // ---- Save ----
  const stamp = new Date().toISOString().slice(0, 10);
  const safeModel = (model || "Unit").replace(/[^a-zA-Z0-9-]/g, "_");
  pdf.save(`ZAMBA_${safeModel}_MaxPerformance_${stamp}.pdf`);
  } finally {
    if (_wasDark5) _htmlEl5.classList.add("dark");
  }
}


